import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Input, FormGroup, Label, Card, CardBody, Badge } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { run, prepareLessonPlanResources } from "../utils/generateAIWarmUps";
import { getAIProviderManager } from "../utils/aiProviderFactory";
import { extractKeywords, calculateWeightedRelevance } from "../utils/keywordExtractor";
import { getSessionId } from "../utils/sessionId";
import { searchOakLessons, fetchOakLessonDetails } from "../utils/oakApi";
import { parseJsonFromText } from "../utils/jsonUtils";
import LessonPlanDisplay from "./LessonPlanDisplay";
import LessonPlanLoader from "./LessonPlanLoader";
import TLLogoLoader from "./TLLogoLoader";
import { updatePlanSection } from "../utils/lessonPlanApi";
import { onAuthStateChanged } from "firebase/auth";
import { functions, db, auth } from "../app/firebase.config";
import warmUpGeneratorImg from "../app/assets/img/warm_up_generator_img.png";
import additionalCriteriaImg from "../app/assets/img/additional_criteria_img.png";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { addGoogleAccount, selectGoogleAccounts, setCurrentUser } from "../features/user/userSlice";
import {
  createNextPlan,
  loadLatestPlan,
  loadPlanTimeline,
  loadCanvasAssignments,
  loadCanvasSubmissions,
} from "../features/lessonPlan/lessonPlanSlice";
import { httpsCallable } from "../utils/firebaseCallable";
import {
  GOOGLE_DRIVE_CONNECTION_UNAVAILABLE_MESSAGE,
  isGoogleDriveConnectionEnabled,
} from "../app/googleDriveFeature";
import {
  hasDriveAccess,
  requestDriveAuthorizationCode,
  exchangeGoogleDriveCode,
  setupDriveFolder,
  getGoogleDriveToken,
} from "../utils/googleDriveAPI";
import {
  getClassAnalytics,
  fetchCanvasIntegrationStatus,
  exportLessonPlanToDrive,
} from "../utils/lessonPlanApi";
import { readDocumentFiles } from "../utils/documentTextExtractor";
import {
  LESSON_PLANNER_CONTEXT_EVENT,
  loadStoredLessonPlannerContext,
} from "../utils/lessonPlannerContext";
import MissingPerspectiveAnalyzer from "./MissingPerspectiveAnalyzer";
import {
  buildPromptCandidatesFromAssignmentAnalysis,
  normalizeAssignmentNameAnalysis,
} from "../utils/assignmentNameAnalysis";
import {
  generateLocalText,
  isLocalPolicyBlockedError,
} from "../utils/localSensitiveLlm";
import { useOllamaSetup } from "../context/OllamaSetupContext";
import {
  PRODUCT_ANALYTICS_EVENTS,
  getBooleanFlag,
  getSizeBucket,
} from "../utils/analyticsEvents";
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  trackProductEvent,
} from "../utils/productAnalytics";

const SUPPORTED_GRADEBOOK_CONTEXT_SOURCES = [
  "gradebook-insights",
  "gradebook-class",
  "header-canvas-course",
];
const LESSON_PLAN_LOADING_MESSAGES = [
  "Analyzing learning objectives...",
  "Designing engaging activities...",
  "Structuring lesson flow...",
  "Adding assessment strategies...",
  "Crafting your lesson plan...",
];
const BLUEPRINT_LOADING_MESSAGES = [
  "Drafting lesson objective...",
  "Identifying key knowledge and skills...",
  "Outlining assessment approach...",
  "Sequencing the lesson arc...",
  "Preparing your lesson blueprint...",
];
const ESSENTIALS_LOADING_MESSAGES = [
  "Reviewing your lesson focus...",
  "Drafting thought-provoking essential questions...",
  "Synthesizing enduring understandings...",
  "Checking alignment between questions and understandings...",
  "Polishing language for classroom discussion...",
];
const DEFAULT_PROMPT_EXAMPLES = [
  "Students will analyze the causes of World War I and evaluate which factors were most significant",
  "Students will examine the Bill of Rights and its impact on American democracy",
  "Students will explore the causes and effects of the Civil Rights Movement",
  "Students will evaluate how primary sources reveal different perspectives on westward expansion",
  "Students will understand how countries respond differently based on different government and economic systems",
];
const ACCOMMODATION_PROMPT_SUGGESTIONS = [
  "Provide sentence frames and vocabulary supports for multilingual learners.",
  "Chunk directions into smaller steps and pair them with visual supports.",
  "Allow extended time and flexible pacing during independent work.",
  "Build in small-group teacher check-ins during guided practice.",
  "Offer multiple ways for students to show understanding orally, visually, or in writing.",
];
const GENERATION_MODE = {
  ADJUST_NON_EDITED: "adjustNonEdited",
  CURRENT_CONTEXT: "currentContext",
};
const VAPORWAVE_EXIT_DURATION_MS = 7000;
const GRADE_OPTIONS = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "College"];
const MATERIAL_PREVIEW_LIMIT = 3;
const GRADE_ORDER_INDEX = new Map(GRADE_OPTIONS.map((grade, index) => [grade, index]));
const normalizeCourseName = (value) => (value || "").trim().toLowerCase();
const normalizePromptText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();
const criteriaIncludesSuggestion = (criteriaText, suggestion) => {
  const normalizedCriteria = normalizePromptText(criteriaText).toLowerCase();
  const normalizedSuggestion = normalizePromptText(suggestion).toLowerCase();
  if (!normalizedCriteria || !normalizedSuggestion) return false;
  return normalizedCriteria.includes(normalizedSuggestion);
};
const appendCriteriaSuggestion = (criteriaText, suggestion) => {
  if (criteriaIncludesSuggestion(criteriaText, suggestion)) {
    return criteriaText;
  }
  const trimmedCriteria = String(criteriaText || "").trim();
  if (!trimmedCriteria) {
    return suggestion;
  }
  return `${trimmedCriteria}\n\n${suggestion}`;
};
const normalizeGradeValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withoutPrefix = raw.replace(/^grade\s+/i, "").trim();
  if (!withoutPrefix) return "";
  if (/^college$/i.test(withoutPrefix)) return "College";
  if (/^k$/i.test(withoutPrefix)) return "K";
  const match = withoutPrefix.match(/^(\d{1,2})(st|nd|rd|th)?$/i);
  if (match) {
    return String(Number(match[1]));
  }
  return withoutPrefix;
};
const createBubbleItems = (list = []) =>
  list.map((text, index) => ({
    id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`,
    text,
    hidden: false,
    expanded: true,
  }));
const createEmptyBubbleItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  text: "",
  hidden: false,
  expanded: true,
});
const getVisibleTexts = (items = []) =>
  items
    .map((item) =>
      typeof item === "string"
        ? { text: item, hidden: false }
        : item
    )
    .filter((item) => !item.hidden && item.text.trim())
    .map((item) => item.text.trim());
const normalizeGradeSelection = (list = []) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list
    .map((grade) => normalizeGradeValue(grade))
    .filter(Boolean)
    .filter((grade) => {
      const key = grade.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => {
      const aIndex = GRADE_ORDER_INDEX.has(a) ? GRADE_ORDER_INDEX.get(a) : Number.POSITIVE_INFINITY;
      const bIndex = GRADE_ORDER_INDEX.has(b) ? GRADE_ORDER_INDEX.get(b) : Number.POSITIVE_INFINITY;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.localeCompare(b);
    });
};
const normalizeClassPeriodLengthValue = (value) =>
  String(value ?? "")
    .replace(/[^\d]/g, "")
    .slice(0, 3);
const getAdaptiveInputWidth = (
  text,
  { minCh = 8, paddingCh = 4 } = {}
) => {
  const length = String(text ?? "").trim().length;
  const widthCh = Math.max(minCh, length + paddingCh);
  return `min(100%, ${widthCh}ch)`;
};
const buildPromptFromEssentialQuestion = (value) => {
  const question = normalizePromptText(value);
  if (!question) return "";
  const normalized = question.endsWith("?") ? question : `${question}?`;
  return `Students will use evidence to answer the essential question: ${normalized}`;
};
const buildPromptFromEnduringUnderstanding = (value) => {
  const understanding = normalizePromptText(value);
  if (!understanding) return "";
  if (/^students?\s+will\s+/i.test(understanding)) {
    return understanding.endsWith(".") ? understanding : `${understanding}.`;
  }
  if (/^understand\s+that\s+/i.test(understanding)) {
    const statement = understanding.replace(/^understand\s+/i, "");
    return `Students will ${statement.endsWith(".") ? statement : `${statement}.`}`;
  }
  const statement = understanding.replace(/[.?]+$/, "");
  return `Students will understand that ${statement}.`;
};
const normalizeClassEssentialsList = (list = []) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object" && typeof item.text === "string") {
        return item.text.trim();
      }
      return "";
    })
    .filter(Boolean)
    .filter((text) => {
      const key = text.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};
const buildClassEssentialsSignature = (
  classId,
  essentialQuestionsList = [],
  enduringUnderstandingsList = []
) =>
  JSON.stringify({
    classId: classId ? String(classId) : "",
    essentialQuestions: essentialQuestionsList,
    enduringUnderstandings: enduringUnderstandingsList,
  });

const normalizeCurriculumTitle = (value) => normalizePromptText(value);

const MAX_DOC_ANALYSIS_FILES = 6;
const MAX_DOC_ANALYSIS_HIGHLIGHTS = 4;
const MAX_DOC_ANALYSIS_TERMS = 10;
const MAX_DOC_ANALYSIS_SIGNAL_LENGTH = 180;
const STANDARD_CODE_PATTERNS = [
  /\bCCSS(?:\.[A-Z]+)?[-A-Z0-9.]+/gi,
  /\bNGSS[-A-Z0-9.]+/gi,
  /\bTEKS[-A-Z0-9.]+/gi,
  /\bISTE[-A-Z0-9.]+/gi,
  /\bC3(?:\.[A-Z0-9-]+)+/gi,
];
const INSTRUCTIONAL_SIGNAL_PATTERNS = [
  { label: "Argument writing", regex: /\b(argue|claim|evidence|counterclaim|thesis)\b/i },
  { label: "Source analysis", regex: /\b(primary source|secondary source|cite|corroborat|sourc)\b/i },
  { label: "Data reasoning", regex: /\b(data|graph|chart|table|statistics|quantitative)\b/i },
  { label: "Discussion protocol", regex: /\b(seminar|discussion|debate|talk move|accountable talk)\b/i },
  { label: "Project-based output", regex: /\b(project|prototype|presentation|artifact|portfolio)\b/i },
  { label: "Reading comprehension", regex: /\b(main idea|infer|theme|comprehension|close read)\b/i },
];

const normalizeDocSignalText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const extractStandardsMentions = (text) => {
  if (!text) return [];
  const found = new Set();
  STANDARD_CODE_PATTERNS.forEach((pattern) => {
    pattern.lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const value = normalizeDocSignalText(match[0]).toUpperCase();
      if (value) {
        found.add(value);
      }
    }
  });
  return Array.from(found).slice(0, 8);
};

const buildUploadedMaterialsAnalysis = (files = []) => {
  const candidateFiles = Array.isArray(files)
    ? files.filter((file) => String(file?.content || "").trim().length > 0)
    : [];
  if (!candidateFiles.length) return null;

  const analyzedFiles = candidateFiles.slice(0, MAX_DOC_ANALYSIS_FILES);
  const joinedText = analyzedFiles
    .map((file) => normalizeDocSignalText(file.content))
    .join("\n\n");

  if (!joinedText) return null;

  const keyConcepts = extractKeywords(joinedText)
    .map((keyword) => normalizeDocSignalText(keyword?.text))
    .filter((term) => term.length >= 3)
    .map((term) => term.toLowerCase())
    .filter((term, index, list) => list.indexOf(term) === index)
    .slice(0, MAX_DOC_ANALYSIS_TERMS)
    .map((term) => term.replace(/\b\w/g, (char) => char.toUpperCase()));

  const instructionalSignals = INSTRUCTIONAL_SIGNAL_PATTERNS.filter((entry) =>
    entry.regex.test(joinedText)
  )
    .map((entry) => entry.label)
    .slice(0, 6);

  const highlights = analyzedFiles
    .map((file) => {
      const cleaned = normalizeDocSignalText(file.content);
      if (!cleaned) return null;
      return {
        fileName: file.name,
        excerpt: cleaned.slice(0, MAX_DOC_ANALYSIS_SIGNAL_LENGTH),
      };
    })
    .filter(Boolean)
    .slice(0, MAX_DOC_ANALYSIS_HIGHLIGHTS);

  const standardsMentions = extractStandardsMentions(joinedText);

  return {
    totalFiles: Array.isArray(files) ? files.length : 0,
    analyzedFiles: analyzedFiles.length,
    keyConcepts,
    instructionalSignals,
    standardsMentions,
    highlights,
  };
};

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const extractMarkdownSectionContent = (markdown, heading) => {
  const source = String(markdown || "");
  if (!source.trim()) return "";
  const pattern = new RegExp(
    `(?:^|\\n)##\\s*${escapeRegExp(heading)}\\s*\\n([\\s\\S]*?)(?=\\n##\\s+|$)`,
    "i"
  );
  const match = source.match(pattern);
  return match ? String(match[1] || "").trim() : "";
};

const summarizeLoopText = (value, maxChars = 360) => {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^\s*[-*]\s*/g, "")
    .trim();
  if (!normalized) return "";
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trim()}...`;
};

const renderPlannerShell = (
  maxWidth,
  children,
  promptState,
  shellRef,
  hideScene = false
) => {
  return (
    <div className="lesson-planner-shell" data-prompt-state={promptState} ref={shellRef}>
      <div
        className={`lesson-planner-shell__scene${
          hideScene ? " lesson-planner-shell__scene--hidden" : ""
        }`}
        aria-hidden="true"
      >
        <div className="palm small">
          <div className="trunk" />
          <div className="trunk-bottom" />
          <div className="leaf-3-s" />
          <div className="leaf-1" />
          <div className="leaf-1-s" />
          <div className="leaf-2" />
          <div className="leaf-2-s" />
          <div className="leaf-3" />
        </div>
        <div className="palm">
          <div className="trunk" />
          <div className="trunk-bottom" />
          <div className="leaf-3-s" />
          <div className="leaf-1" />
          <div className="leaf-1-s" />
          <div className="leaf-2" />
          <div className="leaf-2-s" />
          <div className="leaf-3" />
        </div>
        <div className="delorean demo">
          <div className="mirror-left" />
          <div className="mirror-right" />
          <div className="delorean-cases">
            <div className="wheel-case-left" />
            <div className="wheel-case-right" />
            <div className="wheel-left" />
            <div className="wheel-right" />
          </div>
          <div className="delorean-top">
            <div className="top-lines-on-top" />
            <div className="top-lines-on-top right" />
            <div className="rear-window" />
            <div className="lights">
              <div className="lights-frame" />
              <div className="red-light-left" />
              <div className="red-light-right" />
              <div className="id-container">
                <div className="id" />
              </div>
              <div className="red-reflection" />
                </div>
                <div className="bumper-light-reflection" />
                <div className="bumper-light-reflection right" />
              </div>
              <div className="bumper">
                <div className="bumper-reflection" />
                <div className="bumper-logo" />
                <div className="bumper-hole-1" />
                <div className="bumper-hole-2" />
                <div className="bumper-sticker" />
                <div className="dashes" />
              </div>
              <div className="shadow" />
            </div>
          </div>
        <div className="lesson-planner-shell__inner" style={{ maxWidth }}>
          {children}
        </div>
      </div>
  );
};

const buildKnowledgeLoopSnapshot = ({
  topic,
  lessonPlanMarkdown,
  criteria,
  selectedClassLabel,
}) => {
  const objectiveRaw = extractMarkdownSectionContent(
    lessonPlanMarkdown,
    "OBJECTIVE"
  );
  const keyPointsRaw = extractMarkdownSectionContent(
    lessonPlanMarkdown,
    "KEY POINTS"
  );
  const assessmentRaw = extractMarkdownSectionContent(
    lessonPlanMarkdown,
    "ASSESSMENT"
  );

  const objectiveSummary = summarizeLoopText(
    objectiveRaw || topic,
    380
  );
  const keyPointsSummary = summarizeLoopText(keyPointsRaw, 420);
  const assessmentSummary = summarizeLoopText(assessmentRaw, 380);

  return {
    source: "simple_lesson_planner",
    updatedAt: new Date().toISOString(),
    classLabel: selectedClassLabel || null,
    topic: summarizeLoopText(topic, 220),
    objectiveSummary,
    keyPointsSummary,
    assessmentSummary,
    standards: Array.isArray(criteria?.standards)
      ? criteria.standards.slice(0, 8)
      : [],
    gradeLevels: Array.isArray(criteria?.grades)
      ? criteria.grades.slice(0, 8)
      : [],
    insightFocus: summarizeLoopText(criteria?.insightContext?.focus || "", 140),
    essentialQuestions: Array.isArray(criteria?.essentialQuestions)
      ? criteria.essentialQuestions.slice(0, 4)
      : [],
    enduringUnderstandings: Array.isArray(criteria?.enduringUnderstandings)
      ? criteria.enduringUnderstandings.slice(0, 4)
      : [],
  };
};

const formatLessonBlueprintPrompt = (criteria = {}) => {
  const blueprint =
    criteria && typeof criteria.lessonBlueprint === "object"
      ? criteria.lessonBlueprint
      : null;
  if (!blueprint) return "";

  const objective = normalizePromptText(blueprint.objective);
  const assessment = normalizePromptText(blueprint.assessment);
  const keyPoints = Array.isArray(blueprint.keyPoints)
    ? blueprint.keyPoints.map((item) => normalizePromptText(item)).filter(Boolean)
    : [];
  const lessonArc = Array.isArray(blueprint.lessonArc)
    ? blueprint.lessonArc.map((item) => normalizePromptText(item)).filter(Boolean)
    : [];
  const lines = [];

  if (objective) lines.push(`- Objective: ${objective}`);
  if (keyPoints.length > 0) lines.push(`- Key points: ${keyPoints.join(" | ")}`);
  if (assessment) lines.push(`- Assessment: ${assessment}`);
  if (lessonArc.length > 0) lines.push(`- Lesson arc: ${lessonArc.join(" | ")}`);

  if (lines.length === 0) return "";

  if (criteria?.teacherEditedState?.blueprint) {
    return `Teacher-approved lesson blueprint:\n${lines.join("\n")}\n` +
      "Use this blueprint as the authoritative structure for the lesson plan. Do not change the objective or assessment unless explicitly asked.\n";
  }

  return `Current lesson blueprint draft:\n${lines.join("\n")}\n` +
    "Use this as a planning guide, but improve specificity, pacing, and lesson flow if the draft feels repetitive or too broad.\n";
};

const buildLocalAdaptiveLessonPrompt = ({
  previousLessonPlan,
  topic,
  assignmentTitle,
  assignmentDescription,
  analyticsData,
  criteria,
}) => {
  const lowMastery = Array.isArray(analyticsData?.criteriaAnalysis)
    ? analyticsData.criteriaAnalysis
        .filter((item) => Number(item?.masteryPercentage) < 75)
        .map((item) => item?.criteriaName)
        .filter(Boolean)
    : [];
  const topStruggles = Array.isArray(analyticsData?.commonStruggles)
    ? analyticsData.commonStruggles.slice(0, 4)
    : [];
  const advancedCount = Array.isArray(analyticsData?.advancedStudents)
    ? analyticsData.advancedStudents.length
    : 0;
  const supportCount = Array.isArray(analyticsData?.studentsNeedingSupport)
    ? analyticsData.studentsNeedingSupport.length
    : 0;
  const lessonBlueprintSection = formatLessonBlueprintPrompt(criteria);

  return `Create an adaptive lesson plan for the next class.

Previous lesson topic: ${topic}
Previous lesson plan:
${previousLessonPlan}

${lessonBlueprintSection ? `${lessonBlueprintSection}\n` : ""}Assignment context:
Title: ${assignmentTitle || "Untitled Assignment"}
Description:
${assignmentDescription || "(none)"}

Performance summary:
- Average score: ${Number(analyticsData?.averageScore || 0).toFixed(1)}%
- Median score: ${Number(analyticsData?.medianScore || 0).toFixed(1)}%
- Advanced students: ${advancedCount}
- Students needing support: ${supportCount}
- Low mastery skills: ${lowMastery.join(", ") || "none identified"}
- Common struggles: ${topStruggles
    .map((item) => `${item?.pattern || "unknown"} (${Number(item?.percentage || 0).toFixed(1)}%)`)
    .join("; ") || "none identified"}

Teacher criteria JSON:
${JSON.stringify(criteria || {}, null, 2)}

Return a full lesson plan in markdown with these sections:
## OBJECTIVE
## KEY POINTS
## MATERIALS & RESOURCE ATTRIBUTION
## ASSESSMENT
## OPENING
## INTRODUCTION
## GUIDED PRACTICE
### Advanced Group
### On-Track Group
### Needs Support Group
## INDEPENDENT PRACTICE
## CLOSING`;
};

const SimpleLessonPlanner = ({
  onCourseClick,
  onCurriculumGenerated,
  onGeneratedLessonPlanVisibilityChange,
  onPlannerActivityChange,
  onPlannerLoadingChange,
  onPromptEntryChange,
  shellTitle,
  shellTitleId,
  shellDescription,
}) => {
  const dispatch = useDispatch();
  const [topic, setTopic] = useState("");
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [alignmentStandards, setAlignmentStandards] = useState([]);
  const [schoolDistrict, setSchoolDistrict] = useState("");
  const [classPeriodLength, setClassPeriodLength] = useState("");
  const [classSchoolUrl, setClassSchoolUrl] = useState("");
  const [additionalCriteria, setAdditionalCriteria] = useState(() => {
    // Pre-populate with the Credible showcase's class-insight context if the
    // user just clicked "Use insights in a lesson plan" on /credible/*.
    if (typeof window === "undefined") return "";
    try {
      const stored = window.sessionStorage.getItem(
        "teachleague_credible_lesson_context",
      );
      if (stored && stored.trim()) {
        window.sessionStorage.removeItem(
          "teachleague_credible_lesson_context",
        );
        return stored;
      }
    } catch (error) {
      // ignore — pre-fill is best-effort
    }
    return "";
  });
  const [isCriteriaExpanded, setIsCriteriaExpanded] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      // If we just received insight context, expand the criteria section so
      // the teacher can immediately see what got pulled in.
      return Boolean(
        window.sessionStorage.getItem("teachleague_credible_lesson_context"),
      );
    } catch (error) {
      return false;
    }
  });
  const [isWarmUpCollapsed, setIsWarmUpCollapsed] = useState(false);
  const [udlStrategies, setUdlStrategies] = useState([]);
  const [driveFiles, setDriveFiles] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);
  const [oakSelectedLessons, setOakSelectedLessons] = useState([]);
  const [expandedMaterialGroups, setExpandedMaterialGroups] = useState({
    drive: false,
    local: false,
    oak: false,
  });
  const [currentStage, setCurrentStage] = useState(0);
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [isPromptEditing, setIsPromptEditing] = useState(false);
  const [isUserStateLoading, setIsUserStateLoading] = useState(true);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [, setUploadedViaAttachment] = useState(false);
  const [gradebookImportNotice, setGradebookImportNotice] = useState("");
  const [importedClassContext, setImportedClassContext] = useState(null);
  const [pendingPlannerContextAutogenerate, setPendingPlannerContextAutogenerate] =
    useState(false);
  const [showClassContextSelector, setShowClassContextSelector] = useState(false);

  const [examplePrompts, setExamplePrompts] = useState([]);
  const [showExamples, setShowExamples] = useState(false);
  const [showAccommodationSuggestions, setShowAccommodationSuggestions] = useState(false);
  const [promptSubmissionTick, setPromptSubmissionTick] = useState(0);
  const [submittedTopic, setSubmittedTopic] = useState("");
  const promptInputRef = useRef(null);
  const promptEditedRef = useRef(false);
  const promptRegenTopicRef = useRef("");
  const promptSubmissionTopicRef = useRef("");
  const promptFileInputRef = useRef(null);
  const criteriaInputRef = useRef(null);
  const criteriaFieldRef = useRef(null);
  const lastOakQueryRef = useRef("");
  const classContextAutoOpenRef = useRef(false);
  const classContextAutoCloseTimeoutRef = useRef(null);
  const classContextUserToggleRef = useRef(false);
  const classEssentialsLoadedClassIdRef = useRef(null);
  const classEssentialsSaveTimeoutRef = useRef(null);
  const lastClassEssentialsSignatureRef = useRef(
    buildClassEssentialsSignature("", [], [])
  );
  const lastCanvasDataLoadUidRef = useRef(null);
  const plannerShellRef = useRef(null);
  const handleGenerateRef = useRef(null);

  const [analyticsConsent] = useState(getAnalyticsConsent);

  const [lessonPlanResponse, setLessonPlanResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [promptPlaceholder, setPromptPlaceholder] = useState("");
  const [unitPlanText, setUnitPlanText] = useState("");
  const [unitPlanLoading, setUnitPlanLoading] = useState(false);
  const [unitPlanError, setUnitPlanError] = useState("");
  const [warmUpQuestion, setWarmUpQuestion] = useState("");
  const [warmUpLoading, setWarmUpLoading] = useState(false);
  const [warmUpError, setWarmUpError] = useState("");
  const [showWarmUpInput, setShowWarmUpInput] = useState(false);
  const [includeWarmUp, setIncludeWarmUp] = useState(true);
  const [essentialQuestions, setEssentialQuestions] = useState([]);
  const [enduringUnderstandings, setEnduringUnderstandings] = useState([]);
  const [eqLoading, setEqLoading] = useState(false);
  const [eqError, setEqError] = useState("");
  const [includeEssentials, setIncludeEssentials] = useState(true);
  const [lessonBlueprintObjective, setLessonBlueprintObjective] = useState("");
  const [lessonBlueprintAssessment, setLessonBlueprintAssessment] = useState("");
  const [lessonBlueprintKeyPoints, setLessonBlueprintKeyPoints] = useState([]);
  const [lessonBlueprintArc, setLessonBlueprintArc] = useState([]);
  const [lessonBlueprintWorkspaceItems, setLessonBlueprintWorkspaceItems] = useState([]);
  const [lessonBlueprintArcDragOverId, setLessonBlueprintArcDragOverId] = useState(null);
  const [lessonBlueprintArcDraggingId, setLessonBlueprintArcDraggingId] = useState(null);
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [blueprintError, setBlueprintError] = useState("");
  const [generationMode, setGenerationMode] = useState(
    GENERATION_MODE.ADJUST_NON_EDITED
  );
  const [teacherEditedState, setTeacherEditedState] = useState({
    warmUp: false,
    essentials: false,
    blueprint: false,
  });
  const [prefetchLoading, setPrefetchLoading] = useState(false);
  const [prefetchError, setPrefetchError] = useState("");
  const prefetchedResourcesRef = useRef(null);
  const prefetchKeyRef = useRef("");

  // Follow-up lesson planning state
  const [useAssignmentData, setUseAssignmentData] = useState(false);
  const [previousLessonPlan, setPreviousLessonPlan] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [analyticsId, setAnalyticsId] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [classAnalytics, setClassAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [saveNotice, setSaveNotice] = useState("");
  const [saveNoticeTone, setSaveNoticeTone] = useState("success");
  const [lessonPlanDriveLink, setLessonPlanDriveLink] = useState(null);
  const [isVaporwaveExitActive, setIsVaporwaveExitActive] = useState(false);
  const promptSelectionTimestampRef = useRef(null);
  const promptInputWasVisibleRef = useRef(null);
  const {
    isChecking: ollamaChecking,
    isReady: ollamaReady,
    requiredModel: ollamaRequiredModel,
  } = useOllamaSetup();
  const sensitivePlanningBlocked = Boolean(useAssignmentData) && !ollamaReady;
  const sensitivePlanningBlockedMessage = ollamaChecking
    ? "Checking local Ollama setup. Assignment-data planning will unlock after this check completes."
    : `Using assignment data requires local Ollama with model "${ollamaRequiredModel}". Run "ollama pull ${ollamaRequiredModel}" and recheck setup.`;
  const trimmedTopic = topic.trim();
  const shouldShowWarmUpInput = showWarmUpInput || warmUpQuestion.trim().length > 0;
  const canGenerate = trimmedTopic.length >= 10;
  const hasPrompt = trimmedTopic.length > 0;
  const showPromptInput = isPromptEditing || !hasPrompt;
  const showPromptDisplay = hasPrompt && !showPromptInput;
  const isGeneratingOutput =
    loading || eqLoading || blueprintLoading || warmUpLoading || unitPlanLoading;
  const isVaporWaveTheme =
    typeof document !== "undefined" &&
    document.body.classList.contains("vaporWaveAlt");
  const shouldHoldSunset =
    isVaporWaveTheme && (isVaporwaveExitActive || loading || eqLoading || blueprintLoading || completed);
  const plannerPromptState = shouldHoldSunset
    ? "exiting"
    : showPromptInput
      ? "input"
      : "selected";
  const showPromptHint = showPromptInput && currentStage > 0;
  const plannerShellIntro = shellTitle && !hasPrompt ? (
    <div className="lesson-planner-shell__intro">
      <div className="lesson-planner-shell__heading-row">
        <h1
          id={shellTitleId}
          className="lesson-planner-title lesson-planner-title-grid lesson-planner-shell__title"
        >
          {shellTitle}
        </h1>
      </div>
      {shellDescription ? (
        <p className="lesson-planner-title-support lesson-planner-shell__description">
          {shellDescription}
        </p>
      ) : null}
    </div>
  ) : null;
  const hasPlannerActivity =
    trimmedTopic.length > 0 ||
    currentStage > 0 ||
    previousLessonPlan.trim().length > 0 ||
    localFiles.length > 0 ||
    driveFiles.length > 0 ||
    oakSelectedLessons.length > 0 ||
    Boolean(completed && lessonPlanResponse);
  const showStages =
    currentStage > 0 ||
    trimmedTopic.length >= 10 ||
    previousLessonPlan.trim().length > 0 ||
    localFiles.length > 0 ||
    driveFiles.length > 0;

  useEffect(() => {
    if (currentStage > 0 && hasPrompt) {
      setIsPromptEditing(false);
      setIsPromptFocused(false);
    }
  }, [currentStage, hasPrompt]);

  useEffect(() => {
    if (promptInputWasVisibleRef.current === null) {
      promptInputWasVisibleRef.current = showPromptInput;
      return;
    }
    if (promptInputWasVisibleRef.current && !showPromptInput && hasPrompt) {
      promptSelectionTimestampRef.current = Date.now();
    }
    promptInputWasVisibleRef.current = showPromptInput;
  }, [showPromptInput, hasPrompt]);

  useEffect(() => {
    if (isPromptEditing && promptInputRef.current) {
      promptInputRef.current.focus();
    }
  }, [isPromptEditing]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handleDocumentMouseDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        criteriaFieldRef.current &&
        !criteriaFieldRef.current.contains(target)
      ) {
        setShowAccommodationSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, []);

  const coursesArray = useSelector((state) => state.courses.coursesArray);
  const currentUser = useSelector((state) => state.user.currentUser);
  const currentUserUid = currentUser?.uid || null;
  const googleAccounts = useSelector(selectGoogleAccounts);
  const canOfferDriveConnection =
    hasDriveAccess(currentUser) || isGoogleDriveConnectionEnabled();
  const primaryGoogleAccount = googleAccounts[0] || null;
  const selectedClass = classes.find((item) => item.id === selectedClassId);
  const selectedClassLabel = selectedClassId
    ? selectedClass?.name || "Untitled class"
    : "-- No class selected --";
  const selectedClassDashboardPeriodLength = normalizeClassPeriodLengthValue(
    selectedClass?.classPeriodLength || ""
  );
  const classDurationDisplayText =
    classPeriodLength || selectedClassDashboardPeriodLength || "e.g. 50";
  const selectedAssignment = assignments.find((item) => item.id === assignmentId);
  const selectedAssignmentLabel = selectedAssignment
    ? `${selectedAssignment.title || "Untitled assignment"} (${
        selectedAssignment.gradedSubmissions || 0
      } graded)`
    : "-- Select Assignment --";
  const classSelectorWidth = getAdaptiveInputWidth(selectedClassLabel, {
    minCh: 20,
    paddingCh: 6,
  });
  const classDurationInputWidth = getAdaptiveInputWidth(classDurationDisplayText, {
    minCh: 10,
    paddingCh: 5,
  });
  const assignmentSelectorWidth = getAdaptiveInputWidth(selectedAssignmentLabel, {
    minCh: 24,
    paddingCh: 6,
  });
  const lessonPlansFolderLink =
    primaryGoogleAccount?.lessonPlansFolderLink ||
    currentUser?.teachLeagueLessonPlansFolderLink ||
    null;
  const loadPlannerClasses = useCallback(async () => {
    if (!currentUserUid) {
      setClasses([]);
      return [];
    }
    try {
      const classesRef = collection(db, "teachingCourses");
      const q = query(
        classesRef,
        where("teacherId", "==", currentUserUid),
        where("archived", "==", false),
        limit(200)
      );
      const snapshot = await getDocs(q);
      const list = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((cls) => !cls.canvasListRemoved);
      setClasses(list);
      return list;
    } catch (error) {
      console.error("Error loading classes:", error);
      return [];
    }
  }, [currentUserUid]);

  const coursesById = useMemo(() => {
    const map = new Map();
    (coursesArray || []).forEach((course) => {
      if (course?.id === undefined || course?.id === null) return;
      map.set(String(course.id), course);
    });
    return map;
  }, [coursesArray]);
  const coursesByName = useMemo(() => {
    const map = new Map();
    (coursesArray || []).forEach((course) => {
      if (typeof course?.name !== "string") return;
      const key = normalizeCourseName(course.name);
      if (!key || map.has(key)) return;
      map.set(key, course);
    });
    return map;
  }, [coursesArray]);
  const resolvedSelectedClassCourse = useMemo(() => {
    if (!selectedClass) return null;
    const linkedId = selectedClass.linkedCourseId || selectedClass.courseId;
    if (linkedId && coursesById.has(String(linkedId))) {
      return coursesById.get(String(linkedId));
    }
    const nameKey = normalizeCourseName(
      selectedClass.courseName ||
      selectedClass.name ||
      selectedClass.canvasCourseName
    );
    if (nameKey && coursesByName.has(nameKey)) {
      return coursesByName.get(nameKey);
    }
    return null;
  }, [selectedClass, coursesById, coursesByName]);
  const selectedClassPromptContext = useMemo(() => {
    const classQuestions = normalizeClassEssentialsList(
      selectedClass?.essentialQuestions
    );
    const classUnderstandings = normalizeClassEssentialsList(
      selectedClass?.enduringUnderstandings
    );
    const classStandards = Array.isArray(selectedClass?.standards)
      ? selectedClass.standards
      : [];
    const classGrades = Array.isArray(selectedClass?.gradeLevels)
      ? selectedClass.gradeLevels
      : [];
    const classAssignmentNameAnalysis = normalizeAssignmentNameAnalysis(
      selectedClass?.assignmentNameAnalysis
    );
    const importedAssignmentNameAnalysis = normalizeAssignmentNameAnalysis(
      importedClassContext?.assignmentNameAnalysis
    );

    return {
      className:
        selectedClass?.name ||
        selectedClass?.canvasCourseName ||
        importedClassContext?.className ||
        "",
      linkedCourseId:
        selectedClass?.linkedCourseId ||
        selectedClass?.courseId ||
        importedClassContext?.linkedCourseId ||
        "",
      linkedCourseName:
        selectedClass?.courseName || importedClassContext?.linkedCourseName || "",
      gradeLevels: normalizeGradeSelection(
        selectedGrades.length > 0
          ? selectedGrades
          : classGrades.length > 0
            ? classGrades
            : importedClassContext?.gradeLevels || []
      ),
      standards: (classStandards.length > 0
        ? classStandards
        : importedClassContext?.standards || alignmentStandards
      )
        .map((standard) => String(standard).trim())
        .filter(Boolean),
      essentialQuestions:
        classQuestions.length > 0
          ? classQuestions
          : importedClassContext?.essentialQuestions || [],
      enduringUnderstandings:
        classUnderstandings.length > 0
          ? classUnderstandings
          : importedClassContext?.enduringUnderstandings || [],
      assignmentNameAnalysis:
        classAssignmentNameAnalysis || importedAssignmentNameAnalysis || null,
      knowledgeLoop:
        selectedClass?.knowledgeLoop ||
        importedClassContext?.knowledgeLoop ||
        null,
    };
  }, [selectedClass, importedClassContext, selectedGrades, alignmentStandards]);
  const preferredCourseContext = useMemo(() => {
    const preferredCourse = resolvedSelectedClassCourse;
    const preferredCourseId =
      preferredCourse?.id ||
      selectedClass?.linkedCourseId ||
      selectedClass?.courseId ||
      selectedClassPromptContext?.linkedCourseId ||
      "";
    const preferredCourseName =
      preferredCourse?.name ||
      selectedClass?.courseName ||
      selectedClassPromptContext?.linkedCourseName ||
      selectedClassPromptContext?.className ||
      "";

    return {
      preferredCourseId: preferredCourseId ? String(preferredCourseId).trim() : "",
      preferredCourseName: preferredCourseName ? String(preferredCourseName).trim() : "",
    };
  }, [resolvedSelectedClassCourse, selectedClass, selectedClassPromptContext]);
  const prefetchSignature = useMemo(() => {
    const driveKeys = (driveFiles || [])
      .map((file) => String(file?.id || file?.name || "").trim())
      .filter(Boolean)
      .sort();
    const localKeys = (localFiles || [])
      .map((file) => String(file?.id || file?.name || "").trim())
      .filter(Boolean)
      .sort();
    const oakKeys = (oakSelectedLessons || [])
      .map((lesson) =>
        String(
          lesson?.lessonSlug ||
          lesson?.slug ||
          lesson?.lessonId ||
          lesson?.lesson ||
          lesson?.title ||
          ""
        ).trim()
      )
      .filter(Boolean)
      .sort();
    const grades = (selectedGrades || []).map(String).sort();
    const standards = (alignmentStandards || []).map(String).sort();

    return JSON.stringify({
      topic: trimmedTopic,
      grades,
      standards,
      classPeriodLength,
      additionalCriteria,
      preferredCourseId: preferredCourseContext.preferredCourseId,
      preferredCourseName: preferredCourseContext.preferredCourseName,
      driveKeys,
      localKeys,
      oakKeys,
      includeWarmUp,
      includeEssentials,
      warmUpQuestion: warmUpQuestion.trim(),
      previousLessonPlan: previousLessonPlan.trim().length > 0,
    });
  }, [
    trimmedTopic,
    selectedGrades,
    alignmentStandards,
    classPeriodLength,
    additionalCriteria,
    preferredCourseContext.preferredCourseId,
    preferredCourseContext.preferredCourseName,
    driveFiles,
    localFiles,
    oakSelectedLessons,
    includeWarmUp,
    includeEssentials,
    warmUpQuestion,
    previousLessonPlan,
  ]);
  const uploadedMaterialsAnalysis = useMemo(
    () => buildUploadedMaterialsAnalysis(localFiles),
    [localFiles]
  );
  const lessonPlannerInsightContext = useMemo(() => {
    const trendSections = Array.isArray(
      selectedClassPromptContext.assignmentNameAnalysis?.sections
    )
      ? selectedClassPromptContext.assignmentNameAnalysis.sections
          .slice(0, 3)
          .map((section) => {
            const sectionName = String(section?.sectionName || "").trim();
            const summary = normalizeDocSignalText(section?.summary || "");
            if (!sectionName || !summary) return null;
            const subsection = normalizeDocSignalText(section?.subsection || "");
            const label = subsection
              ? `${sectionName} (${subsection})`
              : sectionName;
            return `${label}: ${summary}`;
          })
          .filter(Boolean)
      : [];

    const context = {
      source: importedClassContext?.source || null,
      focus: importedClassContext?.focus || null,
      className: normalizeDocSignalText(selectedClassPromptContext.className),
      linkedCourseName: normalizeDocSignalText(
        selectedClassPromptContext.linkedCourseName
      ),
      gradeLevels: Array.isArray(selectedClassPromptContext.gradeLevels)
        ? selectedClassPromptContext.gradeLevels.slice(0, 8)
        : [],
      standards: Array.isArray(selectedClassPromptContext.standards)
        ? selectedClassPromptContext.standards.slice(0, 8)
        : [],
      essentialQuestions: Array.isArray(
        selectedClassPromptContext.essentialQuestions
      )
        ? selectedClassPromptContext.essentialQuestions.slice(0, 4)
        : [],
      enduringUnderstandings: Array.isArray(
        selectedClassPromptContext.enduringUnderstandings
      )
        ? selectedClassPromptContext.enduringUnderstandings.slice(0, 4)
        : [],
      assignmentTrendSummary: normalizeDocSignalText(
        selectedClassPromptContext.assignmentNameAnalysis?.summary || ""
      ),
      assignmentTrendSections: trendSections,
      importedTopicSuggestion:
        normalizeDocSignalText(importedClassContext?.topicSuggestion || "") || null,
      importedAdditionalCriteria:
        normalizeDocSignalText(importedClassContext?.additionalCriteria || "").slice(
          0,
          800
        ) || null,
      latestKnowledgeLoopTopic:
        normalizeDocSignalText(
          selectedClassPromptContext?.knowledgeLoop?.topic || ""
        ) || null,
      latestKnowledgeLoopObjective:
        normalizeDocSignalText(
          selectedClassPromptContext?.knowledgeLoop?.objectiveSummary || ""
        ) || null,
      latestKnowledgeLoopAssessment:
        normalizeDocSignalText(
          selectedClassPromptContext?.knowledgeLoop?.assessmentSummary || ""
        ) || null,
    };

    const hasSignal = Object.values(context).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
    return hasSignal ? context : null;
  }, [selectedClassPromptContext, importedClassContext]);
  const autoCurriculumContext = useMemo(() => {
    const context = {};
    const courseIdCandidate =
      resolvedSelectedClassCourse?.id || selectedClassPromptContext?.linkedCourseId;
    if (
      courseIdCandidate !== undefined &&
      courseIdCandidate !== null &&
      String(courseIdCandidate).trim()
    ) {
      context.courseId = String(courseIdCandidate).trim();
    }
    const courseTitle = normalizeCurriculumTitle(
      resolvedSelectedClassCourse?.name ||
        selectedClassPromptContext?.linkedCourseName ||
        ""
    );
    if (courseTitle) {
      context.courseTitle = courseTitle;
    }

    const primaryOakLesson =
      Array.isArray(oakSelectedLessons) && oakSelectedLessons.length > 0
        ? oakSelectedLessons[0]
        : null;
    const unitTitle = normalizeCurriculumTitle(
      primaryOakLesson?.unitTitle || primaryOakLesson?.unit || ""
    );
    if (unitTitle) {
      context.unitTitle = unitTitle;
    }

    const lessonTitle = normalizeCurriculumTitle(
      primaryOakLesson?.lessonTitle || primaryOakLesson?.lesson || ""
    );
    if (lessonTitle) {
      context.lessonTitle = lessonTitle;
    } else {
      const topicTitle = normalizeCurriculumTitle(topic);
      if (topicTitle) {
        context.lessonTitle = topicTitle;
      }
    }

    return Object.keys(context).length > 0 ? context : null;
  }, [resolvedSelectedClassCourse, selectedClassPromptContext, oakSelectedLessons, topic]);
  const curriculumContext = autoCurriculumContext || null;
  const classPromptExamples = useMemo(() => {
    const prompts = [];
    const seen = new Set();
    const addPrompt = (value) => {
      const normalized = normalizePromptText(value);
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      prompts.push(normalized);
    };

    const assignmentDrivenPrompts = buildPromptCandidatesFromAssignmentAnalysis(
      selectedClassPromptContext.assignmentNameAnalysis,
      {
        classLabel:
          selectedClassPromptContext.className ||
          selectedClassPromptContext.linkedCourseName ||
          "this class",
        limit: 6,
      }
    );
    assignmentDrivenPrompts.forEach((prompt) => addPrompt(prompt));

    selectedClassPromptContext.essentialQuestions
      .slice(0, 4)
      .forEach((question) => addPrompt(buildPromptFromEssentialQuestion(question)));
    selectedClassPromptContext.enduringUnderstandings
      .slice(0, 4)
      .forEach((understanding) =>
        addPrompt(buildPromptFromEnduringUnderstanding(understanding))
      );

    let resolvedCourse = null;
    const linkedCourseId = selectedClassPromptContext.linkedCourseId;
    if (linkedCourseId && coursesById.has(String(linkedCourseId))) {
      resolvedCourse = coursesById.get(String(linkedCourseId));
    } else {
      const nameCandidates = [
        selectedClassPromptContext.linkedCourseName,
        selectedClassPromptContext.className,
      ];
      for (const candidate of nameCandidates) {
        const key = normalizeCourseName(candidate);
        if (key && coursesByName.has(key)) {
          resolvedCourse = coursesByName.get(key);
          break;
        }
      }
    }

    if (resolvedCourse) {
      const courseQuestions = normalizeClassEssentialsList(
        resolvedCourse.essentialQuestions
      );
      const courseUnderstandings = normalizeClassEssentialsList(
        resolvedCourse.enduringUnderstandings
      );
      courseQuestions
        .slice(0, 3)
        .forEach((question) => addPrompt(buildPromptFromEssentialQuestion(question)));
      courseUnderstandings
        .slice(0, 3)
        .forEach((understanding) =>
          addPrompt(buildPromptFromEnduringUnderstanding(understanding))
        );
      const courseName =
        typeof resolvedCourse.name === "string"
          ? resolvedCourse.name
          : selectedClassPromptContext.linkedCourseName;
      const normalizedCourseName = normalizePromptText(courseName);
      if (normalizedCourseName) {
        addPrompt(
          `Students will explain the core ideas of ${normalizedCourseName} and support their conclusions with evidence.`
        );
      }
    }

    selectedClassPromptContext.standards.slice(0, 2).forEach((standard) => {
      addPrompt(
        `Students will apply ${standard} by completing a task and justifying their thinking with evidence.`
      );
    });

    const classLabel = normalizePromptText(selectedClassPromptContext.className);
    if (classLabel) {
      addPrompt(
        `Students will identify and explain the most important concepts from ${classLabel}.`
      );
      addPrompt(
        `Students will connect key ideas from ${classLabel} to a contemporary or real-world example.`
      );
    }

    return prompts.slice(0, 8);
  }, [selectedClassPromptContext, coursesById, coursesByName]);
  const plannerGradeOptions = useMemo(
    () => normalizeGradeSelection(GRADE_OPTIONS.concat(selectedGrades)),
    [selectedGrades]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      setIsUserStateLoading(false);
    });
    return unsubscribe;
  }, []);

  // Load classes for the current user
  useEffect(() => {
    void loadPlannerClasses();
  }, [loadPlannerClasses]);

  const applyStoredPlannerContext = useCallback(() => {
    const parsedContext = loadStoredLessonPlannerContext();
    if (
      !parsedContext ||
      !SUPPORTED_GRADEBOOK_CONTEXT_SOURCES.includes(parsedContext.source)
    ) {
      return;
    }

    const shouldAutogenerate = Boolean(parsedContext.autoGenerate);

    if (parsedContext.classId) {
      setSelectedClassId(String(parsedContext.classId));
    }

    const parsedGrades = normalizeGradeSelection(parsedContext.gradeLevels);
    if (parsedGrades.length > 0) {
      setSelectedGrades(parsedGrades);
    }

    const parsedStandards = Array.isArray(parsedContext.standards)
      ? parsedContext.standards
          .map((standard) => String(standard).trim())
          .filter(Boolean)
      : [];
    if (parsedStandards.length > 0) {
      setAlignmentStandards(parsedStandards);
    }

    if (parsedContext.schoolDistrict) {
      setSchoolDistrict(String(parsedContext.schoolDistrict).trim());
    }

    if (parsedContext.classPeriodLength !== undefined && parsedContext.classPeriodLength !== null) {
      setClassPeriodLength(
        normalizeClassPeriodLengthValue(parsedContext.classPeriodLength)
      );
    }

    if (parsedContext.schoolUrl) {
      setClassSchoolUrl(String(parsedContext.schoolUrl).trim());
    }

    const importedEssentials = normalizeClassEssentialsList(
      parsedContext.essentialQuestions
    );
    if (importedEssentials.length > 0) {
      setEssentialQuestions(createBubbleItems(importedEssentials));
    }
    const importedUnderstandings = normalizeClassEssentialsList(
      parsedContext.enduringUnderstandings
    );
    if (importedUnderstandings.length > 0) {
      setEnduringUnderstandings(createBubbleItems(importedUnderstandings));
    }
    const importedAssignmentNameAnalysis = normalizeAssignmentNameAnalysis(
      parsedContext.assignmentNameAnalysis
    );
    setImportedClassContext({
      source: String(parsedContext.source || "").trim() || null,
      classId: parsedContext.classId ? String(parsedContext.classId) : "",
      className: String(
        parsedContext.className || parsedContext.canvasCourseName || ""
      ).trim(),
      linkedCourseId: parsedContext.linkedCourseId
        ? String(parsedContext.linkedCourseId)
        : "",
      linkedCourseName: String(parsedContext.linkedCourseName || "").trim(),
      gradeLevels: parsedGrades,
      standards: parsedStandards,
      essentialQuestions: importedEssentials,
      enduringUnderstandings: importedUnderstandings,
      assignmentNameAnalysis: importedAssignmentNameAnalysis,
      focus: String(parsedContext.focus || "").trim() || null,
      assignmentId: parsedContext.assignmentId
        ? String(parsedContext.assignmentId)
        : null,
      studentKey: parsedContext.studentKey ? String(parsedContext.studentKey) : null,
      topicSuggestion: String(parsedContext.topicSuggestion || "").trim() || null,
      additionalCriteria:
        String(parsedContext.additionalCriteria || "").trim() || null,
      knowledgeLoop:
        parsedContext.knowledgeLoop &&
        typeof parsedContext.knowledgeLoop === "object"
          ? parsedContext.knowledgeLoop
          : null,
    });

    const hasImportedArrays =
      Array.isArray(parsedContext.essentialQuestions) ||
      Array.isArray(parsedContext.enduringUnderstandings);
    const importedEssentialsSource = String(
      parsedContext.essentialsSource || ""
    ).trim().toLowerCase();
    if (parsedContext.classId && hasImportedArrays) {
      classEssentialsLoadedClassIdRef.current = String(parsedContext.classId);
      lastClassEssentialsSignatureRef.current = buildClassEssentialsSignature(
        String(parsedContext.classId),
        importedEssentialsSource === "class" ? importedEssentials : [],
        importedEssentialsSource === "class" ? importedUnderstandings : []
      );
    }

    if (parsedContext.additionalCriteria) {
      const nextCriteria = String(parsedContext.additionalCriteria).trim();
      if (nextCriteria) {
        setAdditionalCriteria((prev) =>
          prev.trim() ? `${prev}\n\n${nextCriteria}` : nextCriteria
        );
      }
    }
    if (
      parsedContext.topicSuggestion &&
      parsedContext.source !== "header-canvas-course"
    ) {
      const suggestedTopic = String(parsedContext.topicSuggestion).trim();
      if (suggestedTopic) {
        setTopic((prev) =>
          shouldAutogenerate ? suggestedTopic : prev.trim() ? prev : suggestedTopic
        );
      }
    }

    const classLabel = parsedContext.className
      ? ` for ${parsedContext.className}`
      : "";
    if (parsedContext.source === "gradebook-insights") {
      setGradebookImportNotice(
        `Imported gradebook insights${classLabel} as planning context. Enter what you want students to know.`
      );
    } else if (parsedContext.source === "header-canvas-course") {
      setGradebookImportNotice(
        `Imported Canvas course context${classLabel}. Enter what you want students to know.`
      );
    } else {
      setGradebookImportNotice(
        `Imported gradebook class context${classLabel}. Enter what you want students to know.`
      );
    }

    setPendingPlannerContextAutogenerate(shouldAutogenerate);
  }, []);

  useEffect(() => {
    applyStoredPlannerContext();
  }, [applyStoredPlannerContext]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handlePlannerContextUpdated = () => {
      applyStoredPlannerContext();
    };

    window.addEventListener(
      LESSON_PLANNER_CONTEXT_EVENT,
      handlePlannerContextUpdated
    );

    return () => {
      window.removeEventListener(
        LESSON_PLANNER_CONTEXT_EVENT,
        handlePlannerContextUpdated
      );
    };
  }, [applyStoredPlannerContext]);

  // Load Canvas assignments and submissions when user is authenticated
  useEffect(() => {
    if (!currentUserUid) {
      lastCanvasDataLoadUidRef.current = null;
      setShowClassContextSelector(false);
      return;
    }
    if (lastCanvasDataLoadUidRef.current === currentUserUid) {
      return;
    }
    lastCanvasDataLoadUidRef.current = currentUserUid;
    const loadCanvasData = async () => {
      try {
        const status = await fetchCanvasIntegrationStatus();
        if (Boolean(status?.connected)) {
          dispatch(loadCanvasAssignments());
          dispatch(loadCanvasSubmissions());
        }
      } catch (err) {
        lastCanvasDataLoadUidRef.current = null;
        // Canvas not configured — silently skip
        console.log("Canvas integration not available:", err.message);
      }
    };
    loadCanvasData();
  }, [currentUserUid, dispatch]);

  // Apply selected class context
  useEffect(() => {
    if (!selectedClassId) {
      classEssentialsLoadedClassIdRef.current = null;
      return;
    }
    setSaveNotice("");
    const cls = classes.find((item) => item.id === selectedClassId);
    if (!cls) return;
    setSelectedGrades(
      normalizeGradeSelection(Array.isArray(cls.gradeLevels) ? cls.gradeLevels : [])
    );
    setAlignmentStandards(Array.isArray(cls.standards) ? cls.standards : []);
    setSchoolDistrict(cls.district || "");
    setClassPeriodLength(normalizeClassPeriodLengthValue(cls.classPeriodLength));
    setClassSchoolUrl(cls.schoolUrl || "");

    if (classEssentialsLoadedClassIdRef.current !== String(selectedClassId)) {
      const classEssentials = normalizeClassEssentialsList(cls.essentialQuestions);
      const classUnderstandings = normalizeClassEssentialsList(
        cls.enduringUnderstandings
      );
      const linkedCourseEssentials =
        classEssentials.length === 0
          ? normalizeClassEssentialsList(
              resolvedSelectedClassCourse?.essentialQuestions
            )
          : [];
      const linkedCourseUnderstandings =
        classUnderstandings.length === 0
          ? normalizeClassEssentialsList(
              resolvedSelectedClassCourse?.enduringUnderstandings
            )
          : [];
      setEssentialQuestions(
        createBubbleItems(
          classEssentials.length > 0
            ? classEssentials
            : linkedCourseEssentials
        )
      );
      setEnduringUnderstandings(
        createBubbleItems(
          classUnderstandings.length > 0
            ? classUnderstandings
            : linkedCourseUnderstandings
        )
      );
      classEssentialsLoadedClassIdRef.current = String(selectedClassId);
      lastClassEssentialsSignatureRef.current = buildClassEssentialsSignature(
        selectedClassId,
        classEssentials,
        classUnderstandings
      );
    }
  }, [selectedClassId, classes, resolvedSelectedClassCourse]);

  const savePlanToTimeline = async ({ content, promptText }) => {
    if (!auth.currentUser) {
      setSaveNoticeTone("error");
      setSaveNotice("Sign in to save plans to the timeline.");
      return false;
    }

    try {
      setSaveNotice("");
      await auth.currentUser.getIdToken(true);
      const planTitle = topic.trim().slice(0, 80) || "Lesson Plan";
      const createResult = await dispatch(
        createNextPlan({
          title: planTitle,
          prompt: null,
          sourceType: "new",
          curriculumContext: curriculumContext || null,
          llmContext: {
            llmSensitivity: "internal",
            llmContextTags: ["lesson_planning"],
          },
        })
      );
      if (createResult.error) {
        const rawMessage =
          createResult.error?.message ||
          createResult.error ||
          "Unable to save plan.";
        const normalized = String(rawMessage || "").toLowerCase();
        let hint = "";
        if (normalized.includes("internal")) {
          hint =
            " Cloud Function createPlan failed. Verify it is deployed and check Firebase logs.";
        } else if (normalized.includes("not found")) {
          hint =
            " Cloud Function createPlan not found. Confirm deployment and region.";
        }
        const message = `${rawMessage}${hint}`;
        setSaveNoticeTone("error");
        setSaveNotice(message);
        return false;
      }

      const plan = createResult.payload?.plan;
      const sections = createResult.payload?.sections || [];
      let resolvedDriveLink = plan?.driveLink || null;
      if (!plan?.id) {
        setSaveNoticeTone("error");
        setSaveNotice("Plan save failed. Try again.");
        return false;
      }

      if (plan && sections.length) {
        const activitiesSection =
          sections.find((section) => section.type === "activities") ||
          sections[0];
        if (activitiesSection?.id) {
          await updatePlanSection(
            plan.id,
            activitiesSection.id,
            content,
            "ai"
          );
        }
      }

      if (typeof content === "string" && content.trim()) {
        try {
          const driveExportResult = await exportLessonPlanToDrive({
            planId: plan.id,
            title: plan.title || planTitle,
            content,
          });
          resolvedDriveLink = driveExportResult?.driveLink || resolvedDriveLink;
        } catch (driveError) {
          console.warn("Lesson plan Drive export skipped:", driveError?.message || driveError);
        }
      }
      setLessonPlanDriveLink(resolvedDriveLink);
      trackProductEvent(PRODUCT_ANALYTICS_EVENTS.PLAN_GENERATED, {
        analytics_attached: getBooleanFlag(classAnalytics),
        artifact_id: plan.id,
        content_length_bucket: getSizeBucket(
          typeof content === "string" ? content.length : 0
        ),
        has_curriculum_context: getBooleanFlag(curriculumContext),
        has_drive_export: getBooleanFlag(resolvedDriveLink),
        has_prompt_text: getBooleanFlag(promptText),
        has_selected_class: getBooleanFlag(selectedClassId),
        topic_length_bucket: getSizeBucket(planTitle.length),
      });

      dispatch(loadLatestPlan());
      dispatch(loadPlanTimeline());
      setSaveNoticeTone("success");
      // setSaveNotice("Saved to timeline.");
      return true;
    } catch (error) {
      console.error("Failed to save plan to timeline:", error);
      setSaveNoticeTone("error");
      setSaveNotice("Could not save to timeline. Try again.");
      return false;
    }
  };

  const syncKnowledgeLoopToClass = async ({ lessonPlanMarkdown, criteria }) => {
    if (!currentUser?.uid || !selectedClassId) return null;
    const snapshot = buildKnowledgeLoopSnapshot({
      topic,
      lessonPlanMarkdown,
      criteria,
      selectedClassLabel:
        selectedClass?.name || selectedClass?.canvasCourseName || null,
    });

    try {
      await updateDoc(doc(db, "teachingCourses", selectedClassId), {
        knowledgeLoop: snapshot,
        updatedAt: serverTimestamp(),
      });
      setClasses((prev) =>
        prev.map((cls) =>
          String(cls.id) === String(selectedClassId)
            ? { ...cls, knowledgeLoop: snapshot }
            : cls
        )
      );
      return true;
    } catch (error) {
      console.error("Failed to sync lesson knowledge loop:", error);
      return false;
    }
  };

  useEffect(() => {
    if (onCurriculumGenerated) {
      onCurriculumGenerated(!!lessonPlanResponse);
    }
  }, [lessonPlanResponse, onCurriculumGenerated]);

  useEffect(() => {
    if (onGeneratedLessonPlanVisibilityChange) {
      onGeneratedLessonPlanVisibilityChange(Boolean(completed && lessonPlanResponse));
    }
  }, [
    completed,
    lessonPlanResponse,
    onGeneratedLessonPlanVisibilityChange,
  ]);

  useEffect(() => {
    if (onPlannerActivityChange) {
      onPlannerActivityChange(hasPlannerActivity);
    }
  }, [hasPlannerActivity, onPlannerActivityChange]);

  useEffect(() => {
    if (onPlannerLoadingChange) {
      onPlannerLoadingChange(isGeneratingOutput);
    }
  }, [isGeneratingOutput, onPlannerLoadingChange]);

  useEffect(() => {
    if (onPromptEntryChange) {
      onPromptEntryChange(hasPrompt);
    }
  }, [hasPrompt, onPromptEntryChange]);

  useEffect(() => {
    setAnalyticsConsent(analyticsConsent);
  }, [analyticsConsent]);

  useEffect(() => {
    const jsonCandidate = essentialQuestions.find(
      (item) => typeof item === "string" && item.includes("\"essentialQuestions\"")
    );
    if (jsonCandidate) {
      const parsed = parseJsonFromText(jsonCandidate);
      const parsedEssentials = Array.isArray(parsed?.essentialQuestions)
        ? parsed.essentialQuestions
        : [];
      const parsedEnduring = Array.isArray(parsed?.enduringUnderstandings)
        ? parsed.enduringUnderstandings
        : [];
      if (parsedEssentials.length || parsedEnduring.length) {
        setEssentialQuestions(createBubbleItems(parsedEssentials));
        setEnduringUnderstandings(createBubbleItems(parsedEnduring));
        return;
      }
    }
    if (essentialQuestions.some((item) => typeof item === "string")) {
      setEssentialQuestions(createBubbleItems(essentialQuestions));
    }
  }, [essentialQuestions]);

  useEffect(() => {
    if (enduringUnderstandings.some((item) => typeof item === "string")) {
      setEnduringUnderstandings(createBubbleItems(enduringUnderstandings));
    }
  }, [enduringUnderstandings]);

  useEffect(() => {
    if (!selectedClassId) {
      classContextAutoOpenRef.current = false;
      if (classContextAutoCloseTimeoutRef.current) {
        clearTimeout(classContextAutoCloseTimeoutRef.current);
        classContextAutoCloseTimeoutRef.current = null;
      }
      return;
    }
    if (classContextAutoOpenRef.current) return;
    classContextAutoOpenRef.current = true;
    classContextUserToggleRef.current = false;
    setShowClassContextSelector(true);
    classContextAutoCloseTimeoutRef.current = setTimeout(() => {
      if (!classContextUserToggleRef.current) {
        setShowClassContextSelector(false);
      }
      classContextAutoCloseTimeoutRef.current = null;
    }, 4000);
    return () => {
      if (classContextAutoCloseTimeoutRef.current) {
        clearTimeout(classContextAutoCloseTimeoutRef.current);
        classContextAutoCloseTimeoutRef.current = null;
      }
    };
  }, [selectedClassId]);

  // Load assignments when using assignment data
  useEffect(() => {
    const loadAssignments = async () => {
      if (currentUserUid && useAssignmentData) {
        setLoadingAssignments(true);
        try {
          const assignmentsRef = collection(db, 'assignments');
          const q = query(
            assignmentsRef,
            where('teacherId', '==', currentUserUid),
            limit(120)
          );

          const assignmentsSnapshot = await getDocs(q);
          const assignmentsList = assignmentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          const toMillis = (value) => {
            if (!value) return 0;
            if (value?.seconds) return Number(value.seconds) * 1000;
            if (typeof value?.toDate === "function") {
              const asDate = value.toDate();
              return asDate instanceof Date ? asDate.getTime() : 0;
            }
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
          };

          const hasPerformanceSignals = (assignment) => {
            const status = String(assignment?.status || "").trim().toLowerCase();
            const gradedSubmissions = Number(assignment?.gradedSubmissions || 0);
            const totalSubmissions = Number(assignment?.totalSubmissions || 0);
            return (
              status === "completed" ||
              gradedSubmissions > 0 ||
              totalSubmissions > 0
            );
          };

          const sortedAssignments = assignmentsList
            .slice()
            .sort((left, right) => {
              const leftGraded = Number(left?.gradedSubmissions || 0);
              const rightGraded = Number(right?.gradedSubmissions || 0);
              if (leftGraded !== rightGraded) {
                return rightGraded - leftGraded;
              }
              const leftTotal = Number(left?.totalSubmissions || 0);
              const rightTotal = Number(right?.totalSubmissions || 0);
              if (leftTotal !== rightTotal) {
                return rightTotal - leftTotal;
              }
              const leftUpdated = toMillis(left?.updatedAt || left?.createdAt);
              const rightUpdated = toMillis(right?.updatedAt || right?.createdAt);
              return rightUpdated - leftUpdated;
            });

          const withSignals = sortedAssignments.filter(hasPerformanceSignals);
          setAssignments((withSignals.length > 0 ? withSignals : sortedAssignments).slice(0, 20));
        } catch (error) {
          console.error('Error loading assignments:', error);
          setAssignments([]);
        } finally {
          setLoadingAssignments(false);
        }
      } else if (!useAssignmentData) {
        setAssignments([]);
        setAssignmentId('');
        setAnalyticsId('');
        setClassAnalytics(null);
      }
    };

    loadAssignments();
  }, [currentUserUid, useAssignmentData]);

  // Load analytics when assignment is selected
  useEffect(() => {
    const loadAnalytics = async () => {
      if (assignmentId && useAssignmentData) {
        setLoadingAnalytics(true);
        try {
          const analytics = await getClassAnalytics(assignmentId);
          setClassAnalytics(analytics);
          setAnalyticsId(analytics.id || assignmentId);
        } catch (error) {
          console.error('Error loading analytics:', error);
          setClassAnalytics(null);
        } finally {
          setLoadingAnalytics(false);
        }
      } else {
        setClassAnalytics(null);
        setAnalyticsId('');
      }
    };

    loadAnalytics();
  }, [assignmentId, useAssignmentData]);

  const ensureClassContextForPlanning = () => {
    return true;
  };

  const handleClassContextSelection = (event) => {
    const nextClassId = event.target.value;
    setSelectedClassId(nextClassId);
  };

  const handleTogglePromptGrade = (grade) => {
    setSelectedGrades((prev) => {
      const existing = normalizeGradeSelection(prev);
      if (existing.includes(grade)) {
        return existing.filter((item) => item !== grade);
      }
      return normalizeGradeSelection(existing.concat(grade));
    });
  };

  const handleClassPeriodLengthChange = (event) => {
    setClassPeriodLength(normalizeClassPeriodLengthValue(event.target.value));
  };


  const handleAddWarmUpBack = () => {
    setIncludeWarmUp(true);
    setShowWarmUpInput(false);
  };

  const handleCriteriaCardClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest("button, .btn, input, textarea, label, a")) return;
    const nextExpanded = !isCriteriaExpanded;
    setIsCriteriaExpanded(nextExpanded);
    if (!nextExpanded) {
      setShowAccommodationSuggestions(false);
    }
    if (nextExpanded && criteriaInputRef.current) {
      criteriaInputRef.current.focus();
    }
  };

  const handleCriteriaInputFocus = () => {
    setIsCriteriaExpanded(true);
    setShowAccommodationSuggestions(true);
  };

  const handleAccommodationSuggestionClick = (suggestion) => {
    setAdditionalCriteria((prev) => appendCriteriaSuggestion(prev, suggestion));
    if (criteriaInputRef.current) {
      criteriaInputRef.current.focus();
    }
  };

  const handleWarmUpCardClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest("button, .btn, input, textarea, label, a")) return;
    setIsWarmUpCollapsed((prev) => !prev);
  };


  const handleGenerate = async () => {
    if (sensitivePlanningBlocked) {
      setSaveNoticeTone("error");
      setSaveNotice(sensitivePlanningBlockedMessage);
      return;
    }

    if (isVaporWaveTheme && hasPrompt) {
      setIsVaporwaveExitActive(true);
      if (showPromptInput) {
        setIsPromptEditing(false);
        setIsPromptFocused(false);
      }
      promptSelectionTimestampRef.current = Date.now();
      await new Promise((resolve) => setTimeout(resolve, VAPORWAVE_EXIT_DURATION_MS));
      setLoading(true);
      setIsVaporwaveExitActive(false);
    } else {
      setLoading(true);
    }

    try {
      const criteria = await buildPlanningCriteria("lessonPlan");
      const blueprintPayload = getLessonBlueprintPayload();
      if (blueprintPayload) {
        criteria.lessonBlueprint = blueprintPayload;
      }

      const metadata = {
        analyticsConsent,
        sessionId: getSessionId(),
        containsStudentPii: Boolean(useAssignmentData),
      };

      let result;
      const precomputedResourcesForRun =
        prefetchKeyRef.current === prefetchSignature
          ? prefetchedResourcesRef.current
          : null;

      const isFollowUp = previousLessonPlan.trim().length > 0;

      if (isFollowUp) {
        if (!previousLessonPlan.trim()) {
          alert('Please paste your previous lesson plan.');
          setLoading(false);
          return;
        }

        if (useAssignmentData) {
          if (!assignmentId) {
            alert('Please select a graded assignment.');
            setLoading(false);
            return;
          }

          // Ensure analytics are loaded
          let analyticsData = classAnalytics;
          if (!analyticsData) {
            try {
              analyticsData = await getClassAnalytics(assignmentId);
              setClassAnalytics(analyticsData);
            } catch (error) {
              console.error('Failed to load analytics:', error);
              alert('Could not load class analytics. Generating lesson plan without analytics data.');
            }
          }

          // Call adaptive lesson plan Cloud Function
          const generateAdaptiveLessonPlan = httpsCallable(functions, 'generateAdaptiveLessonPlan');

          try {
            const adaptiveResult = await generateAdaptiveLessonPlan({
              previousLessonPlan,
              previousLessonTopic: topic,
              assignmentId,
              analyticsId: analyticsData?.id || assignmentId,
              criteria,
              provider: 'gemini',
              llmContext: {
                llmSensitivity: "student_pii",
                llmContextTags: ["gradebook", "adaptive_lesson"],
              },
            });

            result = {
              content: adaptiveResult.data.lessonPlanContent
            };
          } catch (cloudFunctionError) {
            if (!isLocalPolicyBlockedError(cloudFunctionError)) {
              console.error('Cloud Function error:', cloudFunctionError);
              throw new Error(`Failed to generate adaptive lesson plan: ${cloudFunctionError.message}`);
            }

            const localAdaptivePrompt = buildLocalAdaptiveLessonPrompt({
              previousLessonPlan,
              topic,
              assignmentTitle: selectedAssignment?.title || "",
              assignmentDescription: selectedAssignment?.description || "",
              analyticsData: analyticsData || {},
              criteria,
            });
            const localLessonPlanContent = await generateLocalText({
              systemPrompt:
                "You are an expert educator. Produce a practical adaptive lesson plan in markdown.",
              prompt: localAdaptivePrompt,
              temperature: 0.35,
            });

            const persistedAdaptiveResult = await generateAdaptiveLessonPlan({
              previousLessonPlan,
              previousLessonTopic: topic,
              assignmentId,
              analyticsId: analyticsData?.id || assignmentId,
              criteria,
              provider: "ollama",
              llmContext: {
                llmSensitivity: "student_pii",
                llmContextTags: ["gradebook", "adaptive_lesson"],
              },
              localLlmResult: {
                lessonPlanContent: localLessonPlanContent,
                model: process.env.REACT_APP_OLLAMA_MODEL || "llama3.1:8b",
                tokensUsed: {},
              },
            });

            result = {
              content:
                persistedAdaptiveResult?.data?.lessonPlanContent ||
                localLessonPlanContent,
            };
          }
        } else {
          const followUpPrompt = `${topic}\n\nPrevious lesson plan:\n${previousLessonPlan}\n\nPlease suggest the next lesson, building on what was taught.`;
          result = await run(
            followUpPrompt,
            coursesArray,
            "lessonPlan",
            criteria,
            null,
            metadata,
            precomputedResourcesForRun
          );
        }
      } else {
        // Regular lesson plan generation
        const type = "lessonPlan";
        result = await run(
          topic,
          coursesArray,
          type,
          criteria,
          null,
          metadata,
          precomputedResourcesForRun
        );
      }

      if (result) {
        setLessonPlanResponse(result.content);
        setCompleted(true);
        const timelineSaved = await savePlanToTimeline({
          content: result.content,
          promptText: topic.trim() || "Follow-up lesson plan",
        });
        const loopSynced = await syncKnowledgeLoopToClass({
          lessonPlanMarkdown: result.content,
          criteria,
        });
        if (timelineSaved) {
          if (loopSynced === true) {
            setSaveNoticeTone("success");
            setSaveNotice("Saved to timeline and synced class learning target to Dashboard.");
          } else if (loopSynced === false) {
            setSaveNoticeTone("error");
            setSaveNotice(
              "Saved to timeline, but could not sync class learning target to Dashboard."
            );
          } else {
            setSaveNoticeTone("success");
            setSaveNotice("Saved to timeline.");
          }
        }
        await handleGenerateUnitPlan(result.metadata?.curriculumUnitTitles || []);

      }
    } catch (err) {
      console.error(err);
      alert(`Something went wrong: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };
  handleGenerateRef.current = handleGenerate;

  useEffect(() => {
    if (!pendingPlannerContextAutogenerate) return;
    if (loading || eqLoading || blueprintLoading || isUserStateLoading) return;
    if (trimmedTopic.length < 10) return;

    setPendingPlannerContextAutogenerate(false);
    const timeout = setTimeout(() => {
      handleGenerateRef.current?.();
    }, 0);

    return () => clearTimeout(timeout);
  }, [
    pendingPlannerContextAutogenerate,
    loading,
    eqLoading,
    blueprintLoading,
    isUserStateLoading,
    trimmedTopic,
  ]);

  const handleInputFocus = () => {
    const contextualPrompts = classPromptExamples.slice(0, 3);
    const fallbackPrompts = [...DEFAULT_PROMPT_EXAMPLES]
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
    const nextPrompts =
      contextualPrompts.length > 0 ? contextualPrompts : fallbackPrompts;
    setExamplePrompts(nextPrompts);
    setPromptPlaceholder(nextPrompts[0] || "");
    setShowExamples(true);
    ensureClassContextForPlanning();
  };

  const submitPrompt = (nextTopic) => {
    const trimmed = String(nextTopic || "").trim();
    if (!trimmed) return;
    if (eqLoading) return;
    if (currentStage > 0) {
      if (!promptEditedRef.current) return;
      if (trimmed === promptRegenTopicRef.current) {
        promptEditedRef.current = false;
        return;
      }
    }
    promptRegenTopicRef.current = trimmed;
    promptEditedRef.current = false;
    promptSubmissionTopicRef.current = trimmed;
    setPromptSubmissionTick((count) => count + 1);
  };

  const handleExampleClick = (example) => {
    setTopic(example);
    setShowExamples(false);
    submitPrompt(example);
  };

  const handlePromptKeyDown = (event) => {
    if (event.key === "Enter" && event.shiftKey) {
      return;
    }
    const isPlainTab =
      event.key === "Tab" &&
      !event.shiftKey &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey;
    const isPlainEnter =
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey;
    if (!isPlainTab && !isPlainEnter) {
      return;
    }
    if (topic.trim().length > 0) {
      if (!isPlainEnter) {
        return;
      }
      event.preventDefault();
      submitPrompt(topic);
      return;
    }
    if (!isPlainTab) {
      return;
    }
    const suggestedPrompt = promptPlaceholder.trim();
    if (!suggestedPrompt) {
      return;
    }
    event.preventDefault();
    setTopic(suggestedPrompt);
    setShowExamples(false);
    submitPrompt(suggestedPrompt);
  };

  const handlePromptBlur = () => {
    setIsPromptFocused(false);
    setPromptPlaceholder("");
    if (hasPrompt) {
      setIsPromptEditing(false);
    }
  };

  const getRelevantCourse = useCallback(() => {
    if (!topic.trim()) return null;
    const keywords = extractKeywords(topic);
    if (!coursesArray?.length || keywords.length === 0) return null;
    const scored = coursesArray
      .map((course) => {
        const name = course.name || "";
        const intro = course.intro || "";
        const description = typeof course.description === "string"
          ? course.description
          : "";
        const credit = Array.isArray(course.credit)
          ? course.credit.join(" ")
          : course.credit || "";
        const score =
          calculateWeightedRelevance(keywords, name) * 2 +
          calculateWeightedRelevance(keywords, intro) +
          calculateWeightedRelevance(keywords, description) * 0.5 +
          calculateWeightedRelevance(keywords, credit);
        return { course, score };
      })
      .sort((a, b) => b.score - a.score);
    return scored[0]?.score > 0 ? scored[0].course : null;
  }, [coursesArray, topic]);

  const parseListFromText = (text) =>
    text
      .split("\n")
      .map((line) => line.replace(/^[\d\-*.\s]+/, "").trim())
      .filter((line) => line.length > 0);

  const parseDragPayload = (payload) => {
    if (!payload) return null;
    try {
      const parsed = JSON.parse(payload);
      if (parsed && parsed.id) return parsed;
    } catch (error) {
      return { id: payload, source: "arc" };
    }
    return null;
  };

  const handleGenerateBlueprint = async () => {
    if (sensitivePlanningBlocked) {
      setSaveNoticeTone("error");
      setSaveNotice(sensitivePlanningBlockedMessage);
      return;
    }
    if (topic.trim().length < 10) {
      setBlueprintError("Add a learning goal to generate a blueprint.");
      return;
    }
    setBlueprintLoading(true);
    setBlueprintError("");
    try {
      const criteria = await buildPlanningCriteria("lessonBlueprint");
      const metadata = {
        analyticsConsent,
        sessionId: getSessionId(),
        containsStudentPii: Boolean(useAssignmentData),
      };
      const result = await run(
        topic,
        coursesArray,
        "lessonBlueprint",
        criteria,
        null,
        metadata
      );
      const parsed =
        result?.parsed || parseJsonFromText(result?.content || "");
      const normalizeList = (value) => {
        if (Array.isArray(value)) {
          return value.map((item) => String(item).trim()).filter(Boolean);
        }
        if (typeof value === "string") {
          return parseListFromText(value);
        }
        return [];
      };
      if (parsed) {
        const objective = normalizePromptText(
          parsed.objective || parsed.objectiveStatement || parsed.objectives || ""
        );
        const assessment = normalizePromptText(
          parsed.assessment || parsed.assessmentSummary || parsed.assessmentPlan || ""
        );
        const keyPoints = normalizeList(parsed.keyPoints || parsed.key_points || parsed.keypoints);
        const lessonArc = normalizeList(
          parsed.lessonArc || parsed.lesson_arc || parsed.lessonFlow || parsed.lessonOutline
        );
        if (!objective && !assessment && keyPoints.length === 0 && lessonArc.length === 0) {
          setBlueprintError("Lesson blueprint was empty. Please regenerate.");
        } else {
          setLessonBlueprintObjective(objective);
          setLessonBlueprintAssessment(assessment);
          setLessonBlueprintKeyPoints(createBubbleItems(keyPoints));
          setLessonBlueprintArc(createBubbleItems(lessonArc));
          setLessonBlueprintWorkspaceItems([]);
          clearTeacherEdited(["blueprint"]);
          setCurrentStage(2);
        }
      } else {
        setBlueprintError("Couldn't parse lesson blueprint. Please regenerate.");
      }
    } catch (error) {
      console.error("Lesson blueprint generation failed:", error);
      setBlueprintError("Unable to generate lesson blueprint right now.");
    } finally {
      setBlueprintLoading(false);
    }
  };

  const OAK_PUBLIC_LESSON_BASE = "https://www.thenational.academy/teachers/lessons";
  const buildOakPublicLessonUrl = (slug) =>
    slug ? `${OAK_PUBLIC_LESSON_BASE}/${encodeURIComponent(slug)}` : null;
  const resolveOakLessonUrl = (lessonUrl, lessonSlug) => {
    if (!lessonUrl && lessonSlug) {
      return buildOakPublicLessonUrl(lessonSlug);
    }
    if (lessonUrl && lessonUrl.includes("open-api.thenational.academy") && lessonSlug) {
      return buildOakPublicLessonUrl(lessonSlug);
    }
    return lessonUrl || null;
  };

  const getOakLessonKey = (lesson) =>
    lesson?.lessonSlug ||
    lesson?.slug ||
    lesson?.lessonId ||
    lesson?.lesson ||
    lesson?.title;

  const normalizeOakAssets = (assetsData) => {
    const assetsArray = Array.isArray(assetsData)
      ? assetsData
      : assetsData?.assets || assetsData?.lessonAssets || [];
    return assetsArray.map((asset) => ({
      title: asset.title || asset.name || asset.type || "Oak asset",
      url: (() => {
        const rawUrl = asset.url || asset.assetUrl || asset.href || asset.downloadUrl || null;
        if (!rawUrl) return null;
        return rawUrl.includes("open-api.thenational.academy") ? null : rawUrl;
      })(),
      type: asset.type || asset.assetType || null,
    }));
  };

  const normalizeOakLesson = (lesson, details) => {
    const summaryData = details?.summary || {};
    const lessonSlug = lesson.lessonSlug || lesson.slug || summaryData.lessonSlug;
    const lessonUrl = resolveOakLessonUrl(
      lesson.lessonUrl || summaryData.lessonUrl || lesson.url || summaryData.url,
      lessonSlug
    );
    return {
      lessonSlug,
      lessonTitle: lesson.lessonTitle || lesson.title || summaryData.lessonTitle,
      subjectTitle:
        lesson.subjectTitle ||
        lesson.subject ||
        summaryData.subjectTitle ||
        summaryData.subject,
      keyStageTitle:
        lesson.keyStageTitle ||
        lesson.keyStage ||
        summaryData.keyStageTitle ||
        summaryData.keyStage,
      yearTitle: lesson.yearTitle || lesson.year || summaryData.yearTitle || summaryData.year,
      unitTitle: lesson.unitTitle || lesson.unit || summaryData.unitTitle || summaryData.unit,
      lessonUrl,
      summary:
        summaryData.summary ||
        summaryData.lessonSummary ||
        summaryData.description ||
        summaryData.notes ||
        lesson.description ||
        null,
      assets: normalizeOakAssets(details?.assets),
    };
  };

  const buildOakAutoQuery = () => {
    const parts = [
      topic,
      additionalCriteria,
      ...selectedGrades,
      ...alignmentStandards,
    ];
    const query = parts
      .filter((item) => typeof item === "string" && item.trim())
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    return query.length >= 2 ? query : "";
  };

  const loadOakLessonsForPlan = async () => {
    const query = buildOakAutoQuery();
    if (!query) {
      setOakSelectedLessons([]);
      lastOakQueryRef.current = "";
      return [];
    }

    if (query === lastOakQueryRef.current && oakSelectedLessons.length > 0) {
      return oakSelectedLessons;
    }

    try {
      const results = await searchOakLessons({
        query,
        limit: 6,
      });
      const unique = (results || []).filter((lesson, index, list) => {
        const key = getOakLessonKey(lesson);
        if (!key) return true;
        return list.findIndex((item) => getOakLessonKey(item) === key) === index;
      });

      const enriched = await Promise.all(
        unique.slice(0, 4).map(async (lesson) => {
          const lessonKey = getOakLessonKey(lesson);
          if (!lessonKey) return normalizeOakLesson(lesson, null);
          try {
            const details = await fetchOakLessonDetails(lessonKey);
            return normalizeOakLesson(lesson, details);
          } catch (error) {
            console.error("Oak lesson details failed:", error);
            return normalizeOakLesson(lesson, null);
          }
        })
      );

      lastOakQueryRef.current = query;
      setOakSelectedLessons(enriched);
      return enriched;
    } catch (error) {
      console.error("Oak auto-fetch failed:", error);
      setOakSelectedLessons([]);
      return [];
    } finally {
    }
  };

  const buildPlanningCriteria = async (generationType = "lessonPlan") => {
    const autoOakLessons = await loadOakLessonsForPlan();
    const criteria = {
      grades: selectedGrades,
      standards: alignmentStandards,
      district: schoolDistrict,
      classPeriodLength,
      preferredCourseId: preferredCourseContext.preferredCourseId,
      preferredCourseName: preferredCourseContext.preferredCourseName,
      schoolUrl:
        classes.find((c) => c.id === selectedClassId)?.schoolUrl ||
        classSchoolUrl ||
        "",
      additional: additionalCriteria,
      udlStrategies,
      driveFiles,
      localFiles,
      oakLessons: autoOakLessons,
      warmUpQuestion: includeWarmUp ? warmUpQuestion.trim() : "",
      includeWarmUp,
      essentialQuestions: includeEssentials ? getVisibleTexts(essentialQuestions) : [],
      enduringUnderstandings: includeEssentials ? getVisibleTexts(enduringUnderstandings) : [],
      includeEssentials,
      previousLessonPlanProvided: previousLessonPlan.trim().length > 0,
      useAssignmentData: Boolean(useAssignmentData),
      assignmentId: assignmentId || null,
      analyticsId: analyticsId || null,
      insightContext: lessonPlannerInsightContext,
      uploadedMaterialsAnalysis,
      curriculumContext: curriculumContext || null,
      teacherEditedState: { ...teacherEditedState },
    };

    criteria.coherenceGenerationMode = effectiveGenerationMode;
    const teacherAnchors = buildTeacherAnchorContext(generationType);
    if (teacherAnchors) {
      criteria.teacherAnchors = teacherAnchors;
    }

    return criteria;
  };

  useEffect(() => {
    if (currentStage < 1) return;
    if (trimmedTopic.length < 10) return;
    if (!coursesArray || coursesArray.length === 0) return;
    if (
      prefetchKeyRef.current === prefetchSignature &&
      prefetchedResourcesRef.current
    ) {
      return;
    }

    let cancelled = false;

    const prefetchResources = async () => {
      prefetchKeyRef.current = prefetchSignature;
      prefetchedResourcesRef.current = null;
      setPrefetchLoading(true);
      setPrefetchError("");

      try {
        const resources = await prepareLessonPlanResources(
          trimmedTopic,
          coursesArray,
          "lessonPlan",
          preferredCourseContext
        );
        if (cancelled) return;
        prefetchedResourcesRef.current = resources;
      } catch (error) {
        if (cancelled) return;
        console.error("Resource prefetch failed:", error);
        setPrefetchError("Unable to prefetch lesson resources.");
      } finally {
        if (!cancelled) {
          setPrefetchLoading(false);
        }
      }
    };

    void prefetchResources();

    return () => {
      cancelled = true;
    };
  }, [
    currentStage,
    trimmedTopic,
    coursesArray,
    preferredCourseContext,
    prefetchSignature,
  ]);

  const getOakVideoLinks = (lessons) => {
    const seen = new Set();
    const links = [];
    lessons.forEach((lesson) => {
      const lessonTitle =
        lesson.lessonTitle || lesson.title || lesson.lessonSlug || lesson.slug || "Oak lesson";
      const assets = Array.isArray(lesson.assets) ? lesson.assets : [];
      assets.forEach((asset) => {
        const url = asset?.url || "";
        const type = String(asset?.type || "").toLowerCase();
        const isVideo =
          type.includes("video") ||
          /youtube\.com|youtu\.be|vimeo\.com|\.mp4$|\.webm$|\.mov$/i.test(url);
        if (!url || url.includes("open-api.thenational.academy") || !isVideo || seen.has(url)) {
          return;
        }
        seen.add(url);
        const label = asset.title ? `${lessonTitle} • ${asset.title}` : `${lessonTitle} video`;
        links.push({ label, url });
      });
    });
    return links;
  };

  const getLessonBlueprintPayload = () => {
    const objective = normalizePromptText(lessonBlueprintObjective);
    const assessment = normalizePromptText(lessonBlueprintAssessment);
    const keyPoints = getVisibleTexts(lessonBlueprintKeyPoints);
    const lessonArc = getVisibleTexts(lessonBlueprintArc);

    if (!objective && !assessment && keyPoints.length === 0 && lessonArc.length === 0) {
      return null;
    }

    return {
      objective,
      assessment,
      keyPoints,
      lessonArc,
    };
  };

  const markTeacherEdited = useCallback((field) => {
    setTeacherEditedState((prev) =>
      prev[field] ? prev : { ...prev, [field]: true }
    );
  }, []);

  const clearTeacherEdited = useCallback((fields) => {
    setTeacherEditedState((prev) => {
      let changed = false;
      const next = { ...prev };
      fields.forEach((field) => {
        if (next[field]) {
          next[field] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const hasTeacherEditedPlannerContent =
    teacherEditedState.warmUp ||
    teacherEditedState.essentials ||
    teacherEditedState.blueprint;
  const effectiveGenerationMode = hasTeacherEditedPlannerContent
    ? generationMode
    : GENERATION_MODE.CURRENT_CONTEXT;

  const buildTeacherAnchorContext = (generationType) => {
    if (effectiveGenerationMode !== GENERATION_MODE.ADJUST_NON_EDITED) {
      return null;
    }

    const anchors = {};
    const currentAdditionalCriteria = String(additionalCriteria || "").trim();
    const currentWarmUpQuestion = includeWarmUp ? warmUpQuestion.trim() : "";
    const currentEssentialQuestions = includeEssentials
      ? getVisibleTexts(essentialQuestions)
      : [];
    const currentEnduringUnderstandings = includeEssentials
      ? getVisibleTexts(enduringUnderstandings)
      : [];
    const currentBlueprint = getLessonBlueprintPayload();

    if (currentAdditionalCriteria) {
      anchors.additionalCriteria = currentAdditionalCriteria;
    }
    if (teacherEditedState.warmUp && currentWarmUpQuestion) {
      anchors.warmUpQuestion = currentWarmUpQuestion;
    }
    if (teacherEditedState.essentials && currentEssentialQuestions.length > 0) {
      anchors.essentialQuestions = currentEssentialQuestions;
    }
    if (teacherEditedState.essentials && currentEnduringUnderstandings.length > 0) {
      anchors.enduringUnderstandings = currentEnduringUnderstandings;
    }
    if (
      (generationType === "lessonBlueprint" || generationType === "lessonPlan") &&
      teacherEditedState.blueprint &&
      currentBlueprint
    ) {
      anchors.lessonBlueprint = currentBlueprint;
    }

    return Object.keys(anchors).length > 0 ? anchors : null;
  };

  const handleGenerateEssentials = useCallback(async (append = false) => {
    if (topic.trim().length < 10) {
      setEqError("Add a learning goal to generate essential questions.");
      return;
    }
    setEqLoading(true);
    setEqError("");
    let producedEssentials = false;
    try {
      const course = getRelevantCourse();
      const courseCredit = Array.isArray(course?.credit)
        ? course.credit.join(", ")
        : course?.credit || "None";
      const courseIntro = course?.intro || "None";
      const courseDescription = course?.description || "None";
      const courseEssentials = Array.isArray(course?.essentialQuestions)
        ? course.essentialQuestions
        : [];

      const existingQuestionsText = append && essentialQuestions.length > 0
        ? `\n\nExisting essential questions (do NOT repeat these):\n${getVisibleTexts(essentialQuestions).join("\n")}`
        : "";

      const existingUnderstandingsText = append && enduringUnderstandings.length > 0
        ? `\n\nExisting enduring understandings (do NOT repeat these):\n${getVisibleTexts(enduringUnderstandings).join("\n")}`
        : "";

      const countText = append ? "exactly 1" : "exactly 3";
      const prompt = `Generate essential questions and enduring understandings for a lesson plan.

Learning goal:
${topic}

Course context (if relevant):
- Credit: ${courseCredit}
- Intro: ${courseIntro}
- Description: ${courseDescription}
- Existing essential questions: ${courseEssentials.length ? courseEssentials.join(" | ") : "None"}${existingQuestionsText}${existingUnderstandingsText}

Instructions:
- Provide ${countText} essential question${append ? '' : 's'} that ${append ? 'is' : 'are'} open-ended and inquiry-driven.
- Provide ${countText} enduring understanding${append ? '' : 's'} that synthesize${append ? 's' : ''} the big ideas.
- Align to the learning goal and the course context when relevant.
- Return JSON only in this format:
{"essentialQuestions":["..."],"enduringUnderstandings":["..."]}`;

      const aiManager = getAIProviderManager();
      const result = await aiManager.generateStructuredJson({
        prompt,
        metadata: { operation: "generate-essentials" },
        schemaHint:
          '{"essentialQuestions":["..."],"enduringUnderstandings":["..."]}',
      });
      const rawText = result?.raw || result?.content || "";
      const parsed =
        result?.parsed || parseJsonFromText(rawText);
      const looksLikeJson = Boolean(
        rawText && rawText.includes("\"essentialQuestions\"")
      );

      const parsedEssentials = Array.isArray(parsed?.essentialQuestions)
        ? parsed.essentialQuestions
        : [];
      const parsedEnduring = Array.isArray(parsed?.enduringUnderstandings)
        ? parsed.enduringUnderstandings
        : [];

      if (parsedEssentials.length || parsedEnduring.length) {
        if (append) {
          setEssentialQuestions(prev => [...prev, ...createBubbleItems(parsedEssentials)]);
          setEnduringUnderstandings(prev => [...prev, ...createBubbleItems(parsedEnduring)]);
        } else {
          setEssentialQuestions(createBubbleItems(parsedEssentials));
          setEnduringUnderstandings(createBubbleItems(parsedEnduring));
          clearTeacherEdited(["essentials"]);
        }
        producedEssentials = true;
      } else if (rawText && !looksLikeJson) {
        const sections = rawText.split(/enduring understandings?/i);
        const essentials = parseListFromText(sections[0] || "");
        const enduring = parseListFromText(sections[1] || "");
        if (essentials.length || enduring.length) {
          if (append) {
            setEssentialQuestions(prev => [...prev, ...createBubbleItems(essentials)]);
            setEnduringUnderstandings(prev => [...prev, ...createBubbleItems(enduring)]);
          } else {
            setEssentialQuestions(createBubbleItems(essentials));
            setEnduringUnderstandings(createBubbleItems(enduring));
            clearTeacherEdited(["essentials"]);
          }
          producedEssentials = true;
        }
      } else if (rawText && looksLikeJson) {
        setEqError("Couldn't parse essentials response. Please regenerate.");
      }
      if (!append && producedEssentials && currentStage === 0) {
        setIncludeWarmUp(true);
        setCurrentStage(1);
      }
    } catch (error) {
      console.error("Essential questions generation failed:", error);
      setEqError("Unable to generate essential questions right now.");
    } finally {
      setEqLoading(false);
    }
  }, [
    clearTeacherEdited,
    currentStage,
    essentialQuestions,
    enduringUnderstandings,
    getRelevantCourse,
    topic,
  ]);

  useEffect(() => {
    if (promptSubmissionTick === 0) return;
    const trimmedTopic = topic.trim();
    if (!trimmedTopic) return;
    if (trimmedTopic !== promptSubmissionTopicRef.current) {
      return;
    }
    setSubmittedTopic(trimmedTopic);
    promptSubmissionTopicRef.current = "";
    handleGenerateEssentials();
  }, [promptSubmissionTick, topic, handleGenerateEssentials]);

  useEffect(() => {
    if (!submittedTopic) return;
    if (topic.trim() !== submittedTopic) {
      setSubmittedTopic("");
    }
  }, [topic, submittedTopic]);

  const handleWarmUpGenerate = async () => {
    if (!ensureClassContextForPlanning()) return;
    if (topic.trim().length < 10) {
      setWarmUpError("Add a learning goal to generate a warm-up question.");
      return;
    }
    setWarmUpLoading(true);
    setWarmUpError("");
    try {
      const criteria = await buildPlanningCriteria("warmUp");
      const metadata = {
        analyticsConsent,
        sessionId: getSessionId(),
        containsStudentPii: false,
      };
      const precomputedResourcesForRun =
        prefetchKeyRef.current === prefetchSignature
          ? prefetchedResourcesRef.current
          : null;
      const result = await run(
        topic,
        coursesArray,
        "warmUp",
        criteria,
        null,
        metadata,
        precomputedResourcesForRun
      );
      if (result?.content) {
        setWarmUpQuestion(result.content.trim());
        clearTeacherEdited(["warmUp"]);
      }
    } catch (error) {
      console.error("Warm-up generation failed:", error);
      setWarmUpError("Unable to generate a warm-up right now.");
    } finally {
      setWarmUpLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.uid || !selectedClassId) return undefined;
    if (String(classEssentialsLoadedClassIdRef.current || "") !== String(selectedClassId)) {
      return undefined;
    }

    const nextEssentialQuestions = normalizeClassEssentialsList(essentialQuestions);
    const nextEnduringUnderstandings = normalizeClassEssentialsList(
      enduringUnderstandings
    );
    const nextSignature = buildClassEssentialsSignature(
      selectedClassId,
      nextEssentialQuestions,
      nextEnduringUnderstandings
    );

    if (nextSignature === lastClassEssentialsSignatureRef.current) {
      return undefined;
    }

    if (classEssentialsSaveTimeoutRef.current) {
      clearTimeout(classEssentialsSaveTimeoutRef.current);
    }

    classEssentialsSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await updateDoc(doc(db, "teachingCourses", selectedClassId), {
          essentialQuestions: nextEssentialQuestions,
          enduringUnderstandings: nextEnduringUnderstandings,
          updatedAt: serverTimestamp(),
        });
        lastClassEssentialsSignatureRef.current = nextSignature;
        setClasses((prev) =>
          prev.map((cls) =>
            cls.id === selectedClassId
              ? {
                  ...cls,
                  essentialQuestions: nextEssentialQuestions,
                  enduringUnderstandings: nextEnduringUnderstandings,
                }
              : cls
          )
        );
      } catch (error) {
        console.error("Unable to save class essentials:", error);
      }
    }, 900);

    return () => {
      if (classEssentialsSaveTimeoutRef.current) {
        clearTimeout(classEssentialsSaveTimeoutRef.current);
        classEssentialsSaveTimeoutRef.current = null;
      }
    };
  }, [
    currentUser?.uid,
    selectedClassId,
    essentialQuestions,
    enduringUnderstandings,
  ]);

  const toggleAllHidden = (setItems) => {
    setItems((prev) => {
      const allHidden = prev.length > 0 && prev.every((item) => item.hidden);
      return prev.map((item) => ({ ...item, hidden: !allHidden }));
    });
  };

  const escapeHtml = (value) =>
    String(value || "").replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case "\"":
          return "&quot;";
        case "'":
          return "&#39;";
        default:
          return char;
      }
    });

  const linkifyText = (value) => {
    const escaped = escapeHtml(value);
    return escaped.replace(
      /(https?:\/\/[^\s<]+)|(www\.[^\s<]+)/g,
      (match) => {
        const href = match.startsWith("http") ? match : `https://${match}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:var(--color-accent);text-decoration:underline;cursor:pointer;">${match}</a>`;
      }
    );
  };

  const renderEditableBubbles = (
    items,
    setItems,
    emptyLabel,
    bubbleColor,
    onGenerate,
    onManualEdit,
    addLabel = "Create a New Learning Activity"
  ) => (
    <div className="d-flex flex-wrap gap-2 planner-eqeu-bubbles">
      {items
        .filter((item) => !item.hidden)
        .map((item) => (
        <div
          key={item.id}
          className="planner-eqeu-bubble"
          data-bubble-id={item.id}
          style={{
            "--planner-eqeu-fallback-bg": bubbleColor,
            display: "inline-flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            minHeight: "36px",
            maxWidth: "100%",
            flex: "1 1 100%",
          }}
        >
          <div
            className="planner-eqeu-bubble-text"
            contentEditable
            suppressContentEditableWarning
            data-placeholder={!item.text ? `Enter ${emptyLabel}...` : ""}
            onClick={(event) => {
              const target = event.target;
              if (!(target instanceof HTMLElement)) return;
              const link = target.closest("a");
              if (link?.href) {
                event.preventDefault();
                event.stopPropagation();
                window.open(link.href, "_blank", "noopener,noreferrer");
              }
            }}
            onBlur={(event) => {
              const text = event.currentTarget.textContent?.trim() || "";
              if (!text) {
                setItems((prev) => prev.filter((value) => value.id !== item.id));
                if (onManualEdit) {
                  onManualEdit();
                }
              } else if (text !== item.text) {
                setItems((prev) =>
                  prev.map((value) =>
                    value.id === item.id ? { ...value, text } : value
                  )
                );
                if (onManualEdit) {
                  onManualEdit();
                }
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
            style={{
              outline: "none",
              flex: "1 1 auto",
              minWidth: "120px",
              whiteSpace: item.expanded ? "pre-wrap" : "nowrap",
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            dangerouslySetInnerHTML={{ __html: linkifyText(item.text || "") }}
          />
          <button
            type="button"
            onClick={() =>
              setItems((prev) =>
                prev.map((value) =>
                  value.id === item.id
                    ? { ...value, expanded: !value.expanded }
                    : value
                )
              )
            }
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
            aria-label={item.expanded ? "Show less" : "Show more"}
            className="planner-eqeu-bubble-action"
          >
            {item.expanded ? "▾" : "▸"}
          </button>
          <button
            type="button"
            onClick={() =>
              setItems((prev) =>
                prev.map((value) =>
                  value.id === item.id
                    ? { ...value, hidden: !value.hidden }
                    : value
                )
              )
            }
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
            aria-label={item.hidden ? "Show bubble" : "Hide bubble"}
            className="planner-eqeu-bubble-action"
          >
            {item.hidden ? "📕" : "📖"}
          </button>
        </div>
      ))}
      {items.some((item) => item.hidden) && (
        <div
          className="planner-eqeu-hidden-wrap"
          style={{
            width: "100%",
            border: "1px dashed var(--color-surface-border)",
            borderRadius: "12px",
            padding: "0.5rem 0.75rem",
            background: "var(--color-background-alt)",
          }}
        >
          <div className="text-muted small mb-2">
            Hidden {emptyLabel}s
          </div>
          <div className="d-flex flex-wrap gap-2">
            {items
              .filter((item) => item.hidden)
              .map((item) => (
                <button
                  key={item.id}
                  className="planner-eqeu-hidden-chip"
                  type="button"
                  onClick={() =>
                    setItems((prev) =>
                      prev.map((value) =>
                        value.id === item.id
                          ? { ...value, hidden: false }
                          : value
                      )
                    )
                  }
                  style={{
                    border: "1px solid var(--color-surface-border)",
                    background: "var(--color-background)",
                    borderRadius: "999px",
                    padding: "0.2rem 0.6rem",
                    fontSize: "0.8rem",
                    color: "var(--color-text-primary)",
                    cursor: "pointer",
                  }}
                  aria-label={`Restore ${emptyLabel}`}
                >
                  📕 {item.text}
                </button>
              ))}
          </div>
        </div>
      )}
      <div className="d-flex gap-2">
        {onGenerate && (
          <Button
            color="success"
            outline
            size="sm"
            onClick={onGenerate}
            disabled={eqLoading}
          >
            {eqLoading ? "..." : "Generate Another"}
          </Button>
        )}
        <Button
          color="secondary"
          outline
          size="sm"
          onClick={() => {
            const newId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setItems((prev) =>
              prev.concat({
                id: newId,
                text: "",
                hidden: false,
                expanded: true,
              })
            );
            // Focus the new editable bubble after a brief delay
            setTimeout(() => {
              const newBubble = document.querySelector(`[data-bubble-id="${newId}"]`);
              if (newBubble) {
                const editableDiv = newBubble.querySelector('[contenteditable="true"]');
                if (editableDiv instanceof HTMLElement) {
                  editableDiv.focus();
                }
              }
            }, 50);
            if (onManualEdit) {
              onManualEdit();
            }
          }}
        >
          {addLabel}
        </Button>
      </div>
    </div>
  );

  const handleGenerateUnitPlan = async (curriculumUnitTitles = []) => {
    if (!topic.trim()) return;
    setUnitPlanLoading(true);
    setUnitPlanError("");
    try {
      const essentialsBlock = includeEssentials
        ? `Essential Questions: ${
            getVisibleTexts(essentialQuestions).length
              ? getVisibleTexts(essentialQuestions).join(" | ")
              : "None"
          }\nEnduring Understandings: ${
            getVisibleTexts(enduringUnderstandings).length
              ? getVisibleTexts(enduringUnderstandings).join(" | ")
              : "None"
          }`
        : "Essential Questions/Enduring Understandings: Excluded";
      const warmUpBlock = includeWarmUp && warmUpQuestion.trim()
        ? `Warm-Up Question: ${warmUpQuestion.trim()}`
        : "Warm-Up Question: Not included";
      const curriculumBlock = curriculumUnitTitles.length
        ? curriculumUnitTitles.join(" | ")
        : "";
      const curriculumSection = curriculumBlock
        ? `Relevant curriculum unit titles:\n${curriculumBlock}\n\n`
        : "";

      const prompt = `Generate a unit plan placement summary for this lesson.

Learning goal:
${topic}

${essentialsBlock}
${warmUpBlock}

${curriculumSection}Instructions:
- Use the curriculum unit titles above when available.
- Do not invent placeholder unit names.
- If a previous, current, or next unit name is unavailable, omit that line entirely.
- Never write "None" for unit names.
- Return ONLY this exact text format (no angle brackets or braces):
Unit Plan
Previous Unit: Name of previous unit
Current Unit: Name of current unit
Next Unit: Name of next unit`;

      const aiManager = getAIProviderManager();
      const result = await aiManager.generate(prompt);
      if (result?.content) {
        const normalized = result.content
          .replace(/<heading>\s*Unit Plan\s*<\/heading>/gi, "Unit Plan")
          .replace(/[{}]/g, "")
          .trim();
        setUnitPlanText(normalized);
      }
    } catch (error) {
      console.error("Unit plan generation failed:", error);
      setUnitPlanError("Unable to generate unit plan right now.");
    } finally {
      setUnitPlanLoading(false);
    }
  };

  const buildMaterialsContent = () => {
    const lines = [];
    if (driveFiles.length > 0) {
      lines.push("Google Drive Materials:");
      driveFiles.forEach((file) => {
        if (file?.link && file?.name) {
          lines.push(`- [${file.name}](${file.link})`);
        } else if (file?.name) {
          lines.push(`- ${file.name}`);
        }
      });
      lines.push("");
    }

    if (localFiles.length > 0) {
      lines.push("Uploaded Files:");
      localFiles.forEach((file) => {
        if (file?.name) {
          lines.push(`- ${file.name}`);
        }
      });
      lines.push("");
    }

    if (oakSelectedLessons.length > 0) {
      lines.push("Oak Curriculum Lessons (UK/England-based):");
      oakSelectedLessons.forEach((lesson) => {
        const title = lesson.lessonTitle || lesson.title || lesson.lessonSlug || "Oak lesson";
        const link = lesson.lessonUrl || null;
        lines.push(link ? `- [${title}](${link})` : `- ${title}`);
      });
    }

    return lines.join("\n").trim();
  };

  const handleReset = () => {
    setCompleted(false);
    setTopic("");
    setAdditionalCriteria("");
    setUdlStrategies([]);
    setDriveFiles([]);
    setLocalFiles([]);
    setOakSelectedLessons([]);
    setExpandedMaterialGroups({
      drive: false,
      local: false,
      oak: false,
    });
    lastOakQueryRef.current = "";
    setLessonPlanDriveLink(null);
    setLessonPlanResponse("");
    setUnitPlanText("");
    setUnitPlanError("");
    setWarmUpQuestion("");
    setWarmUpError("");
    setShowWarmUpInput(false);
    setIncludeWarmUp(true);
    setEssentialQuestions([]);
    setEnduringUnderstandings([]);
    setEqError("");
    setIncludeEssentials(true);
    setLessonBlueprintObjective("");
    setLessonBlueprintAssessment("");
    setLessonBlueprintKeyPoints([]);
    setLessonBlueprintArc([]);
    setLessonBlueprintWorkspaceItems([]);
    setBlueprintError("");
    setBlueprintLoading(false);
    setGenerationMode(GENERATION_MODE.ADJUST_NON_EDITED);
    setTeacherEditedState({
      warmUp: false,
      essentials: false,
      blueprint: false,
    });
    prefetchedResourcesRef.current = null;
    prefetchKeyRef.current = "";
    setPrefetchLoading(false);
    setPrefetchError("");
    setShowExamples(false);
    setUseAssignmentData(false);
    setPreviousLessonPlan("");
    setAssignmentId("");
    setAnalyticsId("");
    setCurrentStage(0);
    setSelectedClassId("");
    setSelectedGrades([]);
    setAlignmentStandards([]);
    setSchoolDistrict("");
    setClassPeriodLength("");
    setClassSchoolUrl("");
    setUploadedViaAttachment(false);
    setGradebookImportNotice("");

  };

  const applyLocalFiles = (entries) => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    if (!safeEntries.length) return;
    setLocalFiles((prev) => prev.concat(safeEntries));

    const firstText = safeEntries.find((entry) => entry.content);
    if (firstText && !previousLessonPlan.trim()) {
      setPreviousLessonPlan(firstText.content);
    }
  };

  const handlePromptFileInput = async (event) => {
    if (!ensureClassContextForPlanning()) {
      event.target.value = "";
      return;
    }
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const entries = await readDocumentFiles(files, { maxChars: 4000 });
    applyLocalFiles(entries);
    setUploadedViaAttachment(true);
    event.target.value = "";
  };

  const handlePromptDrop = async (event) => {
    if (!ensureClassContextForPlanning()) return;
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files || []);
    if (!files.length) return;
    const entries = await readDocumentFiles(files, { maxChars: 4000 });
    applyLocalFiles(entries);
    setUploadedViaAttachment(true);
  };

  const ensureDriveAccess = async () => {
    if (!hasDriveAccess(currentUser) && !isGoogleDriveConnectionEnabled()) {
      return false;
    }

    const syncDriveFolders = async () => {
      const baseUser = currentUser || {};
      const folderResult = await setupDriveFolder();

      let tokenResult = null;
      try {
        tokenResult = await getGoogleDriveToken();
      } catch (tokenError) {
        console.warn("Drive token fetch failed:", tokenError);
      }

      const accessToken = tokenResult?.accessToken || baseUser.googleAccessToken || null;
      const primaryEmail = primaryGoogleAccount?.email || baseUser.email || null;
      const primaryDisplayName =
        primaryGoogleAccount?.displayName || baseUser.displayName || baseUser.email || "Google Account";
      const primaryPhoto = primaryGoogleAccount?.photoURL || baseUser.photoURL || null;

      if (primaryEmail) {
        dispatch(
          addGoogleAccount({
            email: primaryEmail,
            displayName: primaryDisplayName,
            photoURL: primaryPhoto,
            accessToken,
            tokenTimestamp: accessToken ? Date.now() : null,
            folderId: folderResult?.folderId,
            folderName: folderResult?.folderName,
            folderLink: folderResult?.folderLink,
            lessonPlansFolderId: folderResult?.lessonPlansFolderId,
            lessonPlansFolderName: folderResult?.lessonPlansFolderName,
            lessonPlansFolderLink: folderResult?.lessonPlansFolderLink,
            parentFolderId: folderResult?.parentFolderId,
            parentFolderLink: folderResult?.parentFolderLink,
          })
        );
      }

      dispatch(
        setCurrentUser({
          ...baseUser,
          driveEnabled: true,
          googleAccessToken: accessToken,
          teachLeagueLessonPlansFolderId:
            folderResult?.lessonPlansFolderId || baseUser.teachLeagueLessonPlansFolderId || null,
          teachLeagueLessonPlansFolderName:
            folderResult?.lessonPlansFolderName || baseUser.teachLeagueLessonPlansFolderName || null,
          teachLeagueLessonPlansFolderLink:
            folderResult?.lessonPlansFolderLink || baseUser.teachLeagueLessonPlansFolderLink || null,
        })
      );
    };

    if (hasDriveAccess(currentUser)) {
      if (!currentUser?.uid) {
        return false;
      }
      const hasLessonPlansMetadata = Boolean(
        primaryGoogleAccount?.lessonPlansFolderId ||
          currentUser?.teachLeagueLessonPlansFolderId
      );
      if (!hasLessonPlansMetadata) {
        try {
          await syncDriveFolders();
        } catch (error) {
          console.warn("Drive folder metadata refresh failed:", error);
        }
      }
      return true;
    }
    try {
      if (!currentUser?.uid) {
        return false;
      }

      const code = await requestDriveAuthorizationCode();
      await exchangeGoogleDriveCode(code);
      await syncDriveFolders();
      return true;
    } catch (error) {
      console.error("Failed to connect Google Drive:", error);
    }

    return false;
  };

  const handleOpenLessonPlansFolder = async () => {
    setShowUploadMenu(false);
    let folderLink = lessonPlansFolderLink;

    if (!folderLink) {
      const connected = await ensureDriveAccess();
      if (!connected) {
        alert(
          hasDriveAccess(currentUser)
            ? "Please sign in with Google to connect Drive."
            : GOOGLE_DRIVE_CONNECTION_UNAVAILABLE_MESSAGE
        );
        return;
      }
      try {
        const folderResult = await setupDriveFolder();
        folderLink = folderResult?.lessonPlansFolderLink || folderResult?.folderLink || null;
      } catch (error) {
        console.warn("Could not refresh Lesson Plans folder link:", error);
      }
    }

    if (!folderLink) {
      alert("Could not find your Teach League Lesson Plans folder. Please reconnect Google Drive and try again.");
      return;
    }

    const folderWindow = window.open(folderLink, "_blank", "noopener,noreferrer");
    if (!folderWindow) {
      window.location.assign(folderLink);
    }
  };

  const handleRemoveLocalFile = (id) => {
    setLocalFiles((prev) => prev.filter((file) => file.id !== id));
  };

  const showGenerationLoader =
    (loading || eqLoading || blueprintLoading) && (!isVaporWaveTheme || !isVaporwaveExitActive);
  const hideShellScene = isVaporWaveTheme && showGenerationLoader;
  const activeLoader = loading ? "lessonPlan" : blueprintLoading ? "blueprint" : "essentials";
  const loaderTitle =
    activeLoader === "lessonPlan"
      ? "Generating your lesson plan..."
      : activeLoader === "blueprint"
        ? "Generating your lesson blueprint..."
        : "Generating essential questions and enduring understandings...";
  const loaderStatusMessage =
    activeLoader === "lessonPlan"
      ? "Your lesson plan is being generated. Review the context above while you wait."
      : activeLoader === "blueprint"
        ? "Your lesson blueprint is being generated. Review the context above while you wait."
        : "Your essential questions and enduring understandings are being generated.";
  const loaderMessages =
    activeLoader === "lessonPlan"
      ? LESSON_PLAN_LOADING_MESSAGES
      : activeLoader === "blueprint"
        ? BLUEPRINT_LOADING_MESSAGES
        : ESSENTIALS_LOADING_MESSAGES;
  const hasLessonBlueprint = Boolean(getLessonBlueprintPayload());

  const renderGenerationActionPanel = ({
    maxWidth = "860px",
    primaryAction,
    secondaryAction = null,
  }) => (
    <div
      className="mx-auto mb-3"
      style={{
        maxWidth,
        textAlign: "left",
      }}
    >
      <div className="d-flex flex-wrap justify-content-center gap-2">
        {secondaryAction && (
          <Button
            color={secondaryAction.color || "secondary"}
            outline={secondaryAction.outline}
            size={secondaryAction.size || "lg"}
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            title={secondaryAction.title}
          >
            {secondaryAction.loading
              ? secondaryAction.loadingLabel || "Working..."
              : secondaryAction.label}
          </Button>
        )}
        <Button
          color={primaryAction.color || "primary"}
          outline={primaryAction.outline}
          size={primaryAction.size || "lg"}
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          title={primaryAction.title}
        >
          {primaryAction.loading
            ? primaryAction.loadingLabel || "Working..."
            : primaryAction.label}
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    // Keep generation flows in view without yanking first-load visitors past the hero.
    if (!showGenerationLoader) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const scrollToTop = () => {
      const shellNode = plannerShellRef.current;
      if (shellNode?.scrollIntoView) {
        shellNode.scrollIntoView({ behavior: "auto", block: "start" });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };
    const rafId = requestAnimationFrame(scrollToTop);
    return () => cancelAnimationFrame(rafId);
  }, [showGenerationLoader]);

  const renderContextSummary = ({ showTopic = false } = {}) => {
    const visibleEssentialQuestions = getVisibleTexts(essentialQuestions);
    const visibleEnduringUnderstandings = getVisibleTexts(enduringUnderstandings);
    const warmUpText = warmUpQuestion.trim();
    const gradeSummary =
      selectedGrades.length > 0
        ? selectedGrades
        : selectedClassPromptContext?.gradeLevels || [];
    const standardsSummary =
      alignmentStandards.length > 0
        ? alignmentStandards
        : selectedClassPromptContext?.standards || [];
    const classLength =
      classPeriodLength || selectedClassDashboardPeriodLength || "";
    const chipStyle = {
      padding: "0.25rem 0.6rem",
      borderRadius: "999px",
      background: "var(--color-background-section)",
      fontSize: "0.75rem",
    };
    const renderMaterialPreviewGroup = (groupKey, label, items, renderItem) => {
      if (!items.length) return null;

      const isExpanded = expandedMaterialGroups[groupKey];
      const visibleItems = isExpanded
        ? items
        : items.slice(0, MATERIAL_PREVIEW_LIMIT);
      const hiddenCount = Math.max(items.length - MATERIAL_PREVIEW_LIMIT, 0);

      return (
        <div className="mb-2">
          <div className="text-muted small">
            {label} ({items.length})
          </div>
          {visibleItems.map(renderItem)}
          {hiddenCount > 0 && (
            <button
              type="button"
              className="text-muted small"
              onClick={() =>
                setExpandedMaterialGroups((prev) => ({
                  ...prev,
                  [groupKey]: !prev[groupKey],
                }))
              }
              aria-expanded={isExpanded}
              style={{
                background: "none",
                border: 0,
                padding: 0,
                cursor: "pointer",
              }}
            >
              {isExpanded ? "Show less" : `+${hiddenCount} more`}
            </button>
          )}
        </div>
      );
    };

    return (
      <div className="mb-4">
        <div
          style={{
            background: "var(--color-background-alt)",
            borderRadius: "12px",
            border: "1px solid var(--color-surface-border)",
            padding: "1rem",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: "0.75rem",
              color: "var(--color-primary-dark)",
            }}
          >
            Planning Context
          </div>
          {showTopic && trimmedTopic && (
            <div className="mb-2">
              <span className="text-muted small">Topic</span>
              <div style={{ fontWeight: 500 }}>{trimmedTopic}</div>
            </div>
          )}
          <div className="d-flex flex-wrap gap-2 mb-2">
            {gradeSummary.length > 0 && (
              <span style={chipStyle}>Grades: {gradeSummary.join(", ")}</span>
            )}
            {standardsSummary.length > 0 && (
              <span style={chipStyle}>Standards: {standardsSummary.join(", ")}</span>
            )}
            {classLength && (
              <span style={chipStyle}>Class length: {classLength} min</span>
            )}
          </div>
          {additionalCriteria.trim() && (
            <div className="mb-2">
              <div className="text-muted small">Additional criteria</div>
              <div style={{ fontSize: "0.85rem" }}>
                {additionalCriteria.trim().slice(0, 220)}
              </div>
            </div>
          )}
          {warmUpText.length > 0 && (
            <div className="mb-2">
              <div className="text-muted small">Warm-up question</div>
              <div style={{ fontSize: "0.85rem" }}>{warmUpText}</div>
            </div>
          )}
          {(visibleEssentialQuestions.length > 0 ||
            visibleEnduringUnderstandings.length > 0) && (
            <div className="planner-eqeu-grid mt-3">
              {visibleEssentialQuestions.length > 0 && (
                <div className="planner-eqeu-panel">
                  <div className="text-muted small mb-2 planner-eqeu-heading">
                    Essential Questions
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {visibleEssentialQuestions.map((question, index) => (
                      <span
                        key={`${question}-${index}`}
                        style={{
                          padding: "0.4rem 0.8rem",
                          borderRadius: "16px",
                          background: "var(--color-background-section)",
                          fontSize: "0.85rem",
                          flex: "1 1 100%",
                        }}
                      >
                        {question}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {visibleEnduringUnderstandings.length > 0 && (
                <div className="planner-eqeu-panel">
                  <div className="text-muted small mb-2 planner-eqeu-heading">
                    Enduring Understandings
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {visibleEnduringUnderstandings.map((understanding, index) => (
                      <span
                        key={`${understanding}-${index}`}
                        style={{
                          padding: "0.4rem 0.8rem",
                          borderRadius: "16px",
                          background: "var(--color-background-alt)",
                          fontSize: "0.85rem",
                          flex: "1 1 100%",
                        }}
                      >
                        {understanding}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {(driveFiles.length > 0 || localFiles.length > 0 || oakSelectedLessons.length > 0) && (
            <div className="mt-3">
              <div className="text-muted small mb-2">Materials</div>
              {renderMaterialPreviewGroup("drive", "Drive materials", driveFiles, (file) => (
                <div key={file.id || file.name} style={{ fontSize: "0.85rem" }}>
                  {file?.link ? (
                    <a href={file.link} target="_blank" rel="noopener noreferrer">
                      {file.name}
                    </a>
                  ) : (
                    file.name
                  )}
                </div>
              ))}
              {renderMaterialPreviewGroup("local", "Uploaded files", localFiles, (file) => (
                <div key={file.id || file.name} style={{ fontSize: "0.85rem" }}>
                  {file.name}
                </div>
              ))}
              {renderMaterialPreviewGroup("oak", "Oak lessons", oakSelectedLessons, (lesson) => {
                const title =
                  lesson.lessonTitle || lesson.title || lesson.lessonSlug || "Oak lesson";
                const link = lesson.lessonUrl || null;
                return (
                  <div key={title} style={{ fontSize: "0.85rem" }}>
                    {link ? (
                      <a href={link} target="_blank" rel="noopener noreferrer">
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPromptSection = ({
    readOnly = false,
    hideUpload = false,
    showHint = false,
  } = {}) => {
    const shouldRenderPrompt = showPromptInput || hasPrompt;
    const promptDisplayClassName = hideUpload
      ? "lesson-planner-prompt-display"
      : "lesson-planner-prompt-display lesson-planner-prompt-display--with-upload";
    if (!shouldRenderPrompt) return null;

    return (
      <>
        <div
          className={`lesson-planner-prompt${showPromptDisplay ? " lesson-planner-prompt--display" : ""}${
            showHint ? " lesson-planner-prompt--with-hint" : ""
          }`}
          style={{ position: "relative" }}
          onMouseLeave={() => {
            if (!isPromptFocused) {
              setShowUploadMenu(false);
            }
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={readOnly ? undefined : handlePromptDrop}
        >
          {showPromptInput && !readOnly ? (
            <Input
              className="planner-vapor-field lesson-planner-prompt-input"
              type="text"
              value={topic}
              onChange={(e) => {
                const nextValue = e.target.value;
                setTopic(nextValue);
                promptEditedRef.current = true;
                if (nextValue.trim().length > 0) {
                  setShowExamples(false);
                }
              }}
              onFocus={() => {
                setIsPromptEditing(true);
                setIsPromptFocused(true);
                handleInputFocus();
              }}
              onBlur={handlePromptBlur}
              onKeyDown={handlePromptKeyDown}
              placeholder={isPromptFocused ? promptPlaceholder : ""}
              innerRef={promptInputRef}
            />
          ) : showPromptDisplay || hasPrompt ? (
            readOnly ? (
              <div className={`${promptDisplayClassName} lesson-planner-prompt-display--static`}>
                {trimmedTopic}
              </div>
            ) : (
              <button
                type="button"
                className={promptDisplayClassName}
                onClick={() => setIsPromptEditing(true)}
                aria-label="Edit lesson prompt"
                title="Edit lesson prompt"
              >
                {trimmedTopic}
              </button>
            )
          ) : null}
          {!hideUpload ? (
            <>
              <button
                type="button"
                onClick={() => {
                  if (!ensureClassContextForPlanning()) return;
                  setShowUploadMenu((prev) => !prev);
                }}
                className={`lesson-planner-prompt-upload-button${
                  showUploadMenu ? " is-open" : ""
                }`}
                aria-label="Upload lesson plan"
                title="Upload lesson plan"
              >
                <span
                  className="lesson-planner-prompt-upload-button__icon"
                  aria-hidden="true"
                >
                  +
                </span>
              </button>
              {showUploadMenu && (
                <div className="lesson-planner-prompt-upload-menu">
                  <button
                    type="button"
                    onClick={() => {
                      if (!ensureClassContextForPlanning()) return;
                      if (promptFileInputRef.current) {
                        promptFileInputRef.current.click();
                      }
                      setShowUploadMenu(false);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      textAlign: "left",
                      padding: "0.45rem 0.6rem",
                      borderRadius: "8px",
                      color: "var(--color-text-primary)",
                      cursor: "pointer",
                    }}
                  >
                    Upload from computer
                  </button>
                  {canOfferDriveConnection ? (
                    <button
                      type="button"
                      onClick={handleOpenLessonPlansFolder}
                      style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        padding: "0.45rem 0.6rem",
                        borderRadius: "8px",
                        color: "var(--color-text-primary)",
                        cursor: "pointer",
                      }}
                    >
                      Open Lesson Plans folder
                    </button>
                  ) : null}
                </div>
              )}
              <input
                ref={promptFileInputRef}
                type="file"
                multiple
                onChange={handlePromptFileInput}
                style={{ display: "none" }}
              />
            </>
          ) : null}
        </div>
        {showHint ? (
          <div className="lesson-planner-prompt-hint">
            Press Enter to regenerate essential questions and enduring understandings. Press Tab to use the suggested prompt when the field is empty.
          </div>
        ) : null}
      </>
    );
  };

  if (completed && lessonPlanResponse) {
    const relevantCourse = getRelevantCourse();
    const hasVideoLinks = (course) =>
      Boolean(course?.youtube || course?.extrayoutube || course?.extrayoutube1);
    const fallbackCourse = coursesArray.find((course) => hasVideoLinks(course));
    const videoSourceCourse = hasVideoLinks(relevantCourse)
      ? relevantCourse
      : fallbackCourse;
    const oakVideoLinks = getOakVideoLinks(oakSelectedLessons);
    const videoLinks = [
      videoSourceCourse?.youtube
        ? {
            label: `YouTube Playlist • ${videoSourceCourse.name || "Course"}`,
            url: videoSourceCourse.youtube,
          }
        : null,
      videoSourceCourse?.extrayoutube
        ? {
            label: `Related Playlist • ${videoSourceCourse.name || "Course"}`,
            url: videoSourceCourse.extrayoutube,
          }
        : null,
      videoSourceCourse?.extrayoutube1
        ? {
            label: `Extension Playlist • ${videoSourceCourse.name || "Course"}`,
            url: videoSourceCourse.extrayoutube1,
          }
        : null,
      ...oakVideoLinks,
    ].filter(Boolean);

    return renderPlannerShell(
      "1100px",
      <>
        {plannerShellIntro}
        {renderPromptSection({ readOnly: true, hideUpload: true })}
        <LessonPlanDisplay
          response={lessonPlanResponse}
          courses={coursesArray}
          onCourseClick={onCourseClick}
          lessonBlueprint={getLessonBlueprintPayload()}
          lessonBlueprintAuthoritative={teacherEditedState.blueprint}
          warmUpQuestion={warmUpQuestion}
          includeWarmUp={includeWarmUp}
          essentialQuestions={getVisibleTexts(essentialQuestions)}
          enduringUnderstandings={getVisibleTexts(enduringUnderstandings)}
          includeEssentials={includeEssentials}
          unitPlanText={unitPlanText}
          unitPlanLoading={unitPlanLoading}
          unitPlanError={unitPlanError}
          materialsContent={buildMaterialsContent()}
          videoLinks={videoLinks}
          showVideos
          lessonPlanDriveLink={lessonPlanDriveLink}
          lessonPlansFolderLink={lessonPlansFolderLink}
          onOpenLessonPlansFolder={handleOpenLessonPlansFolder}
          onUpdateResponse={setLessonPlanResponse}
          onStartNewLessonPlan={handleReset}
          showDashboardLink={Boolean(currentUser)}
        />
        <div className="mt-4">
          <MissingPerspectiveAnalyzer
            eyebrow="Voices Review"
            title="Missing Voices In This Lesson Plan"
            description="This review runs against the current lesson-plan markdown so you can see whose voices are centered, what is absent, and whether you want to regenerate the plan with a fuller range of voices."
            sourceLabel="generated lesson plan"
            initialText={lessonPlanResponse}
            initialDocuments={localFiles}
            editableText={false}
            allowUploads={false}
            autoAnalyze
            analyzeButtonLabel="Analyze This Lesson Plan"
            rerunButtonLabel="Re-run Voices Review"
            applyPrompt="Would you like to regenerate this lesson plan so it includes the missing voices and resource leads below?"
            onApplyRecommendations={async ({ lessonPlan }) => {
              setLessonPlanResponse(lessonPlan);
            }}
          />
        </div>
      </>,
      plannerPromptState,
      plannerShellRef,
      hideShellScene
    );
  }

  if (showGenerationLoader) {
    return renderPlannerShell(
      "900px",
      <>
        {plannerShellIntro}
        {hasPrompt ? renderPromptSection({ readOnly: true, hideUpload: true }) : null}
        {renderContextSummary()}
        <div className="text-muted small mb-3 text-center">{loaderStatusMessage}</div>
        <LessonPlanLoader title={loaderTitle} messages={loaderMessages} />
      </>,
      plannerPromptState,
      plannerShellRef,
      hideShellScene
    );
  }

  return renderPlannerShell(
    "960px",
    <>
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: var(--color-text-light);
          font-style: italic;
          pointer-events: none;
        }
      `}</style>
      <div className="lesson-planner-entry-header">
        {currentUser && showClassContextSelector && (
          <div className="planner-class-context-panel">
            <Card className="mb-3 planner-class-context-card">
              <CardBody className="planner-class-context-body">
                <div className="d-flex flex-wrap gap-2 align-items-center planner-class-context-layout">
                  <Label className="fw-bold mb-0 planner-class-context-heading">Class context</Label>
                  {classes.length > 0 ? (
                    <Input
                      className="planner-vapor-field planner-class-context-select"
                      type="select"
                      value={selectedClassId}
                      onChange={handleClassContextSelection}
                      style={{ width: classSelectorWidth }}
                    >
                      <option value="">-- No class selected --</option>
                      {classes.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.name || "Untitled class"}
                        </option>
                      ))}
                    </Input>
                  ) : (
                    <span className="text-muted small planner-class-context-note">
                      No classes are available yet.
                    </span>
                  )}
                  {selectedClassId && (
                    <div className="planner-class-context-details" style={{ width: "100%" }}>
                      <div className="text-muted small mb-1 mt-2 planner-class-context-subheading">
                        Grade context
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        {plannerGradeOptions.map((grade) => {
                          const isSelected = selectedGrades.includes(grade);
                          return (
                            <Label
                              key={grade}
                              className="planner-class-grade-chip"
                              check
                              style={{
                                padding: "0.2rem 0.6rem",
                                border: isSelected
                                  ? "2px solid var(--grade-chip-border-selected)"
                                  : "1px solid var(--grade-chip-border)",
                                borderRadius: "6px",
                                cursor: "pointer",
                                background: isSelected
                                  ? "var(--grade-chip-bg-selected)"
                                  : "var(--grade-chip-bg)",
                                fontSize: "0.85rem",
                                color: isSelected
                                  ? "var(--grade-chip-text-selected)"
                                  : "var(--grade-chip-text)",
                              }}
                            >
                              <Input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleTogglePromptGrade(grade)}
                                style={{ display: "none" }}
                              />
                              {grade}
                            </Label>
                          );
                        })}
                      </div>
                      <div className="mt-2 planner-class-duration" style={{ width: classDurationInputWidth }}>
                        <Label
                          for="planner-class-period-length"
                          className="text-muted small mb-1 planner-class-context-subheading"
                        >
                          Class duration (minutes)
                        </Label>
                        <Input
                          className="planner-vapor-field planner-class-context-duration"
                          id="planner-class-period-length"
                          type="number"
                          min="1"
                          max="300"
                          step="1"
                          value={classPeriodLength}
                          onChange={handleClassPeriodLengthChange}
                          placeholder={selectedClassDashboardPeriodLength || "e.g. 50"}
                          style={{ width: "100%" }}
                        />
                        <div className="text-muted small mt-1 planner-class-context-note">
                          {selectedClassDashboardPeriodLength
                            ? `Defaults to ${selectedClassDashboardPeriodLength} min from the dashboard, but you can change it for this lesson.`
                            : "Set the class duration for this lesson."}
                        </div>
                      </div>
                      <div className="text-muted small mt-1 planner-class-context-note">
                        Change these settings in the{" "}
                        <Link className="planner-class-context-link" to="/dashboard">
                          dashboard
                        </Link>{" "}
                        to update future plans.
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-2 planner-class-context-badges">
                        {alignmentStandards.map((std) => (
                          <Badge
                            key={std}
                            color="secondary"
                            pill
                            className="planner-class-context-badge planner-class-context-badge--standard"
                          >
                            {std}
                          </Badge>
                        ))}
                        {schoolDistrict && (
                          <Badge
                            color="light"
                            pill
                            className="planner-class-context-badge planner-class-context-badge--district"
                          >
                            {schoolDistrict}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {!selectedClassId && classes.length > 0 && (
                  <div className="text-muted small mt-2 planner-class-context-note">
                    Select a class to auto-apply grades, standards, district, period length, and school URL.
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        )}

        {saveNotice && (
          <div
            style={{
              textAlign: "center",
              color: saveNoticeTone === "error" ? "var(--color-error)" : "var(--color-success)",
              fontSize: "0.9rem",
              marginBottom: "0.75rem",
            }}
          >
            {saveNotice}
          </div>
        )}
        {gradebookImportNotice && (
          <div
            style={{
              textAlign: "center",
              color: "var(--color-primary-dark)",
              fontSize: "0.82rem",
              marginBottom: "0.75rem",
            }}
          >
            {gradebookImportNotice}
          </div>
        )}
        {plannerShellIntro}
      </div>
      {isUserStateLoading ? (
        <div className="text-center py-4">
          <TLLogoLoader label={null} size={56} />
        </div>
      ) : (
        renderPromptSection({ showHint: showPromptHint })
      )}
      {!hasPrompt && (
        <div
          className={`lesson-planner-suggestions mt-2 mb-3${showExamples ? " is-visible" : ""}`}
          aria-hidden={!showExamples}
        >
          {showExamples && examplePrompts.length > 0 && (
            <div className="d-flex flex-column gap-2">
              {examplePrompts.map((example, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => handleExampleClick(example)}
                  style={{
                    textAlign: "left",
                    whiteSpace: "normal",
                    padding: "0.6rem 0",
                    border: "none",
                    borderBottom: "1px solid var(--color-surface-border)",
                    borderRadius: 0,
                    transition: "color 0.2s ease, background-color 0.2s ease",
                    backgroundColor: "transparent",
                    color: "var(--color-text-secondary)",
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(var(--color-primary-rgb), 0.12)";
                    e.currentTarget.style.color = "var(--color-primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--color-text-secondary)";
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {currentUser && submittedTopic && topic.trim().length >= 10 && currentStage === 0 && (
        <div
          className="mt-4 p-4"
          style={{
            background: "var(--color-background-alt)",
            borderRadius: "12px",
            border: "1px solid var(--color-surface-border)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 style={{ marginBottom: 0, color: "var(--color-primary-dark)" }}>
              Essential Questions & Enduring Understandings
            </h5>
            <Button
              color="success"
              size="sm"
              onClick={() => handleGenerateEssentials(false)}
              disabled={eqLoading}
            >
              {eqLoading ? (
                <span className="d-inline-flex align-items-center">
                  <TLLogoLoader label={null} size={16} layout="inline" className="me-1" />
                  Generating...
                </span>
              ) : (
                "Regenerate"
              )}
            </Button>
          </div>
          {eqError && <div className="text-danger small mb-2">{eqError}</div>}
          {essentialQuestions.length === 0 && enduringUnderstandings.length === 0 && (
            <div className="text-muted small mb-3">No essentials yet.</div>
          )}
          <div className="mb-3 planner-eqeu-grid">
            <div className="planner-eqeu-panel">
              <div className="planner-eqeu-heading-row mb-1">
                <div className="text-muted small planner-eqeu-heading">Essential Questions</div>
                {essentialQuestions.length > 0 && (
                  <button
                    className="planner-eqeu-toggle"
                    type="button"
                    onClick={() => toggleAllHidden(setEssentialQuestions)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "var(--color-text-secondary)",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                    }}
                    aria-label="Toggle all essential questions"
                  >
                    {essentialQuestions.length > 0 &&
                    essentialQuestions.every((item) => item.hidden)
                      ? "📕 Show all"
                      : "📖 Hide all"}
                  </button>
                )}
              </div>
              {renderEditableBubbles(
                essentialQuestions,
                setEssentialQuestions,
                "question",
                "var(--color-background-section)",
                () => handleGenerateEssentials(true),
                () => markTeacherEdited("essentials"),
                "Add another question"
              )}
            </div>
            <div className="planner-eqeu-panel">
              <div className="planner-eqeu-heading-row mb-1">
                <div className="text-muted small planner-eqeu-heading">Enduring Understandings</div>
                {enduringUnderstandings.length > 0 && (
                  <button
                    className="planner-eqeu-toggle"
                    type="button"
                    onClick={() => toggleAllHidden(setEnduringUnderstandings)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "var(--color-text-secondary)",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                    }}
                    aria-label="Toggle all enduring understandings"
                  >
                    {enduringUnderstandings.length > 0 &&
                    enduringUnderstandings.every((item) => item.hidden)
                      ? "📕 Show all"
                      : "📖 Hide all"}
                  </button>
                )}
              </div>
              {renderEditableBubbles(
                enduringUnderstandings,
                setEnduringUnderstandings,
                "understanding",
                "var(--color-background-alt)",
                () => handleGenerateEssentials(true),
                () => markTeacherEdited("essentials"),
                "Add another understanding"
              )}
            </div>
          </div>
        </div>
      )}

      {showStages && (
        <>
          <div className="d-flex justify-content-center mt-4 gap-2">
            {currentStage < 1 && (
              <Button
                color="success"
                onClick={() => setCurrentStage((prev) => Math.min(1, prev + 1))}
              >
                Next →
              </Button>
            )}
          </div>

          {currentStage === 1 && (
            <div className="mt-4">
              {(essentialQuestions.length > 0 || enduringUnderstandings.length > 0) && (
                <div
                  className="mb-4 p-3"
                  style={{
                    background: "var(--color-background)",
                    borderRadius: "10px",
                    border: "1px solid var(--color-surface-border)",
                  }}
                >
                  <div className="planner-eqeu-grid">
                    {essentialQuestions.length > 0 && (
                      <div className="planner-eqeu-panel">
                        <div className="planner-eqeu-heading-row mb-1">
                          <div className="text-muted small planner-eqeu-heading">Essential Questions</div>
                          <button
                            className="planner-eqeu-toggle"
                            type="button"
                            onClick={() => toggleAllHidden(setEssentialQuestions)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "var(--color-text-secondary)",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                            }}
                            aria-label="Toggle all essential questions"
                          >
                            {essentialQuestions.length > 0 &&
                            essentialQuestions.every((item) => item.hidden)
                              ? "📕 Show all"
                              : "📖 Hide all"}
                          </button>
                        </div>
                        {renderEditableBubbles(
                          essentialQuestions,
                          setEssentialQuestions,
                          "question",
                          "var(--color-background-section)",
                          () => handleGenerateEssentials(true),
                          () => markTeacherEdited("essentials"),
                          "Add another question"
                        )}
                      </div>
                    )}
                    {enduringUnderstandings.length > 0 && (
                      <div className="planner-eqeu-panel">
                        <div className="planner-eqeu-heading-row mb-1">
                          <div className="text-muted small planner-eqeu-heading">Enduring Understandings</div>
                          <button
                            className="planner-eqeu-toggle"
                            type="button"
                            onClick={() => toggleAllHidden(setEnduringUnderstandings)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "var(--color-text-secondary)",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                            }}
                            aria-label="Toggle all enduring understandings"
                          >
                            {enduringUnderstandings.length > 0 &&
                            enduringUnderstandings.every((item) => item.hidden)
                              ? "📕 Show all"
                              : "📖 Hide all"}
                          </button>
                        </div>
                        {renderEditableBubbles(
                          enduringUnderstandings,
                          setEnduringUnderstandings,
                          "understanding",
                          "var(--color-background-alt)",
                          () => handleGenerateEssentials(true),
                          () => markTeacherEdited("essentials"),
                          "Add another understanding"
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {previousLessonPlan.trim().length > 0 && (
                <div
                  className="mb-4 p-3"
                  style={{
                    background: "var(--color-background)",
                    borderRadius: "10px",
                    border: "1px solid var(--color-surface-border)",
                  }}
                >
                  <FormGroup check className="mb-3">
                    <Label check style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                      <Input
                        type="checkbox"
                        checked={useAssignmentData}
                        onChange={(e) => setUseAssignmentData(e.target.checked)}
                      />{" "}
                      Use grading data to inform next lesson
                    </Label>
                    <div className="text-muted small mt-1">
                      Select a graded assignment to see class performance analytics and get data-driven lesson recommendations.
                    </div>
                  </FormGroup>

                  {useAssignmentData && (
                    <>
                      {sensitivePlanningBlocked && (
                        <div
                          className="mb-3 p-2"
                          style={{
                            borderRadius: "8px",
                            border: "1px solid var(--color-warning, #f59e0b)",
                            background: "var(--color-background-section)",
                            color: "var(--color-text-primary)",
                          }}
                        >
                          <div className="small">{sensitivePlanningBlockedMessage}</div>
                        </div>
                      )}
                      <FormGroup>
                        <Label className="fw-bold">Select Assignment with Performance Data</Label>
                        {loadingAssignments ? (
                          <div className="text-muted small">
                            <TLLogoLoader label={null} size={18} layout="inline" className="me-2" /> Loading assignments...
                          </div>
                        ) : assignments.length === 0 ? (
                          <div className="text-muted small">
                            No assignments with submission data found. Grade or sync submissions first.
                          </div>
                        ) : (
                          <Input
                            type="select"
                            value={assignmentId}
                            onChange={(e) => setAssignmentId(e.target.value)}
                            style={{
                              width: assignmentSelectorWidth,
                              borderRadius: "6px",
                              border: "1px solid var(--color-surface-border)",
                            }}
                          >
                            <option value="">-- Select Assignment --</option>
                            {assignments.map((assignment) => (
                              <option key={assignment.id} value={assignment.id}>
                                {assignment.title} ({assignment.gradedSubmissions || 0} graded / {assignment.totalSubmissions || 0} submitted)
                              </option>
                            ))}
                          </Input>
                        )}
                      </FormGroup>

                      {loadingAnalytics && (
                        <div className="mt-3 text-center text-muted">
                          <TLLogoLoader label={null} size={18} layout="inline" className="me-2" /> Loading analytics...
                        </div>
                      )}

                      {classAnalytics && !loadingAnalytics && (
                        <div
                          className="mt-3 p-3"
                          style={{
                            background: "var(--color-background-alt)",
                            borderRadius: "8px",
                            border: "1px solid var(--color-surface-border)",
                          }}
                        >
                          <div className="fw-bold mb-2" style={{ color: "var(--color-primary-dark)" }}>
                            Class Performance Summary
                          </div>
                          <div className="small">
                            <div className="mb-2">
                              <strong>Average Score:</strong> {classAnalytics.stats?.averageScore?.toFixed(1)}% ({classAnalytics.stats?.passRate?.toFixed(0)}% pass rate)
                            </div>
                            {classAnalytics.errorPatterns?.commonErrors?.length > 0 && (
                              <div>
                                <strong>Top Challenge:</strong> {classAnalytics.errorPatterns.commonErrors[0].issue} ({classAnalytics.errorPatterns.commonErrors[0].percentage?.toFixed(0)}% of students)
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="planner-image-card-grid mb-4">
                <div
                  className={`planner-image-card planner-image-card--wide${isWarmUpCollapsed ? " is-collapsed" : ""}`}
                  style={{ backgroundImage: `url(${warmUpGeneratorImg})` }}
                  onClick={handleWarmUpCardClick}
                >
                  <div className="planner-image-card__content">
                    {includeWarmUp ? (
                      <FormGroup className="mb-0">
                        {!isWarmUpCollapsed && (
                          <div className="warmup-heading planner-warmup-detail">
                            Warm-ups, Bellringers, Skill Drills, Class Openers, Bellwork, Do Nows,
                            Entry Tickets, Welcome Work, Questions of the Day.
                          </div>
                        )}
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                          {!isWarmUpCollapsed && (
                            <span className="text-muted small planner-warmup-option-label">Optionally:</span>
                          )}
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => {
                              setIsWarmUpCollapsed(false);
                              handleWarmUpGenerate();
                            }}
                            disabled={warmUpLoading}
                          >
                            {warmUpLoading ? (
                              <span className="d-inline-flex align-items-center">
                                <TLLogoLoader label={null} size={16} layout="inline" className="me-1" />
                                Generating...
                              </span>
                            ) : (
                              "Generate Warm-Up"
                            )}
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            onClick={() => {
                              setIsWarmUpCollapsed(false);
                              setShowWarmUpInput(true);
                            }}
                            disabled={warmUpLoading}
                          >
                            Write Your Own
                          </Button>
                        </div>
                        {!isWarmUpCollapsed && (
                          <>
                            <div className="text-muted small mt-2 planner-warmup-detail">
                              Have your students begin class with a daily warm-up question that activates their
                              prior understanding and provides an entry point into the day's lesson.
                            </div>
                            {shouldShowWarmUpInput && (
                              <Input
                                className="planner-vapor-field planner-vapor-field--warmup mt-2 planner-warmup-detail"
                                type="textarea"
                                rows="3"
                                value={warmUpQuestion}
                                onChange={(event) => {
                                  setWarmUpQuestion(event.target.value);
                                  markTeacherEdited("warmUp");
                                }}
                                placeholder="Write a warm-up question that activates prior knowledge."
                                style={{
                                  borderRadius: "6px",
                                  border: "1px solid var(--color-surface-border)",
                                  backgroundColor: "var(--color-background)",
                                }}
                              />
                            )}
                            {warmUpError && (
                              <div className="text-danger small mt-2 planner-warmup-detail">
                                {warmUpError}
                              </div>
                            )}
                          </>
                        )}
                      </FormGroup>
                    ) : (
                      <div>
                        {!isWarmUpCollapsed && (
                          <>
                            <div className="planner-warmup-detail" style={{ fontWeight: 600 }}>
                              Warm-up skipped
                            </div>
                            <div className="text-muted small mb-2 planner-warmup-detail">
                              You chose to continue without a Bellringer. You can still add one before generating.
                            </div>
                          </>
                        )}
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => {
                            setIsWarmUpCollapsed(false);
                            handleAddWarmUpBack();
                          }}
                          disabled={warmUpLoading}
                        >
                          {warmUpLoading ? "Generating..." : "Add Bellringer"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`planner-image-card planner-image-card--criteria${isWarmUpCollapsed ? " planner-image-card--wide" : ""}${isCriteriaExpanded ? " is-expanded" : ""}`}
                  style={{ backgroundImage: `url(${additionalCriteriaImg})` }}
                  onClick={handleCriteriaCardClick}
                >
                  <div className="planner-image-card__content">
                    <FormGroup className="mb-0 planner-image-card__criteria-group">
                      <Label className="fw-bold planner-image-card__label">Additional Criteria</Label>
                      <div
                        className="planner-image-card__criteria-field"
                        ref={criteriaFieldRef}
                      >
                        <Input
                          className="planner-vapor-field planner-vapor-field--criteria planner-image-card__criteria-input"
                          type="textarea"
                          value={additionalCriteria}
                          onChange={(e) => setAdditionalCriteria(e.target.value)}
                          onFocus={handleCriteriaInputFocus}
                          onClick={handleCriteriaInputFocus}
                          placeholder="e.g., Include accommodations for ELL students, focus on hands-on activities..."
                          rows="3"
                          innerRef={criteriaInputRef}
                          style={{ borderRadius: "6px", border: "1px solid var(--color-surface-border)" }}
                        />
                        {showAccommodationSuggestions && (
                          <div className="planner-criteria-suggestions">
                            <div className="planner-criteria-suggestions__header">
                              <div className="planner-criteria-suggestions__title">
                                Suggested accommodations
                              </div>
                              <div className="planner-criteria-suggestions__meta">
                                Select more than one
                              </div>
                            </div>
                            <div className="planner-criteria-suggestions__list">
                              {ACCOMMODATION_PROMPT_SUGGESTIONS.map((suggestion) => {
                                const isSelected = criteriaIncludesSuggestion(
                                  additionalCriteria,
                                  suggestion
                                );
                                return (
                                  <button
                                    type="button"
                                    key={suggestion}
                                    className={`planner-criteria-suggestion${isSelected ? " is-selected" : ""}`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleAccommodationSuggestionClick(suggestion)}
                                    aria-pressed={isSelected}
                                  >
                                    {suggestion}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </FormGroup>
                  </div>
                </div>
              </div>

              {localFiles.length > 0 && (
                <div className="mt-3">
                  <div className="text-muted small mb-2">Uploaded materials</div>
                  {localFiles.map((file) => (
                    <div
                      key={file.id}
                      className="d-flex justify-content-between align-items-center mb-1"
                    >
                      <span>{file.name}</span>
                      <Button
                        color="link"
                        size="sm"
                        onClick={() => handleRemoveLocalFile(file.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  {uploadedMaterialsAnalysis && (
                    <div
                      className="small mt-2"
                      style={{
                        background: "rgba(15, 23, 42, 0.04)",
                        border: "1px solid var(--color-surface-border)",
                        borderRadius: "8px",
                        padding: "0.55rem 0.65rem",
                      }}
                    >
                      <div className="fw-semibold text-dark">Upload analysis</div>
                      <div className="text-muted">
                        {uploadedMaterialsAnalysis.analyzedFiles} of{" "}
                        {uploadedMaterialsAnalysis.totalFiles} file(s) analyzed for
                        planning signals.
                      </div>
                      {uploadedMaterialsAnalysis.keyConcepts.length > 0 && (
                        <div className="text-muted mt-1">
                          Concepts: {uploadedMaterialsAnalysis.keyConcepts.join(", ")}
                        </div>
                      )}
                      {uploadedMaterialsAnalysis.standardsMentions.length > 0 && (
                        <div className="text-muted mt-1">
                          Standards found:{" "}
                          {uploadedMaterialsAnalysis.standardsMentions.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {currentStage === 1 && (
            <div className="text-center mt-4">
              {renderGenerationActionPanel({
                maxWidth: "760px",
                primaryAction: {
                  label: "Generate Lesson Blueprint",
                  loadingLabel: "Generating Blueprint...",
                  onClick: handleGenerateBlueprint,
                  disabled:
                    !canGenerate ||
                    sensitivePlanningBlocked ||
                    isVaporwaveExitActive ||
                    blueprintLoading,
                  title: sensitivePlanningBlocked
                    ? sensitivePlanningBlockedMessage
                    : undefined,
                  loading: blueprintLoading,
                },
              })}
              {prefetchLoading && (
                <div className="text-muted small mt-2">
                  Prefetching lesson resources in the background...
                </div>
              )}
              {prefetchError && (
                <div className="text-danger small mt-2">{prefetchError}</div>
              )}
            </div>
          )}

          {currentStage === 2 && (
            <div className="mt-4">
              <div
                className="mb-4 p-4"
                style={{
                  background: "var(--color-background)",
                  borderRadius: "12px",
                  border: "1px solid var(--color-surface-border)",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 style={{ marginBottom: "0.25rem", color: "var(--color-primary-dark)" }}>
                      Lesson Blueprint
                    </h5>
                    <div className="text-muted small">
                      Review and edit the blueprint before generating the full lesson plan.
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => setCurrentStage(1)}
                    >
                      Back
                    </Button>
                  </div>
                </div>
                {blueprintError && (
                  <div className="text-danger small mb-3">{blueprintError}</div>
                )}
                <FormGroup className="mb-3">
                  <Label className="fw-bold">Objective</Label>
                  <Input
                    className="planner-vapor-field"
                    type="textarea"
                    rows="2"
                    value={lessonBlueprintObjective}
                    onChange={(event) => {
                      setLessonBlueprintObjective(event.target.value);
                      markTeacherEdited("blueprint");
                    }}
                    placeholder="Students will be able to..."
                    style={{
                      borderRadius: "6px",
                      border: "1px solid var(--color-surface-border)",
                    }}
                  />
                </FormGroup>
                <div className="mb-3">
                  <div className="mb-2">
                    <Label className="fw-bold mb-0">Key Points</Label>
                  </div>
                  {renderEditableBubbles(
                    lessonBlueprintKeyPoints,
                    setLessonBlueprintKeyPoints,
                    "key point",
                    "var(--color-background-section)",
                    null,
                    () => markTeacherEdited("blueprint"),
                    "Add key point"
                  )}
                </div>
                <FormGroup className="mb-3">
                  <Label className="fw-bold">Assessment</Label>
                  <Input
                    className="planner-vapor-field"
                    type="textarea"
                    rows="2"
                    value={lessonBlueprintAssessment}
                    onChange={(event) => {
                      setLessonBlueprintAssessment(event.target.value);
                      markTeacherEdited("blueprint");
                    }}
                    placeholder="How will students demonstrate mastery?"
                    style={{
                      borderRadius: "6px",
                      border: "1px solid var(--color-surface-border)",
                    }}
                  />
                </FormGroup>
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label className="fw-bold mb-0">Lesson Arc</Label>
                  </div>
                  <div className="lesson-blueprint-arc-layout">
                    <div className="lesson-blueprint-arc">
                      {lessonBlueprintArc
                        .filter((item) => !item.hidden)
                        .map((item) => {
                        const dropId = `before-${item.id}`;
                        return (
                        <React.Fragment key={item.id}>
                      <div
                        className={`lesson-blueprint-arc-dropzone${lessonBlueprintArcDragOverId === dropId ? " is-drag-over" : ""}`}
                        onDragEnter={() => setLessonBlueprintArcDragOverId(dropId)}
                        onDragLeave={() =>
                          setLessonBlueprintArcDragOverId((prev) => (prev === dropId ? null : prev))
                        }
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          const payload = parseDragPayload(
                            event.dataTransfer.getData("text/plain")
                          );
                          if (!payload?.id) return;
                          setLessonBlueprintArc((prev) => {
                            let next = [...prev];
                            let moved = null;
                            if (payload.source === "workspace") {
                              setLessonBlueprintWorkspaceItems((workspacePrev) => {
                                const workspaceNext = [...workspacePrev];
                                const wsIndex = workspaceNext.findIndex(
                                  (value) => value.id === payload.id
                                );
                                if (wsIndex === -1) return workspacePrev;
                                [moved] = workspaceNext.splice(wsIndex, 1);
                                return workspaceNext;
                              });
                            } else {
                              const fromIndex = next.findIndex((value) => value.id === payload.id);
                              if (fromIndex === -1) return prev;
                              [moved] = next.splice(fromIndex, 1);
                            }
                            if (!moved) return prev;
                            const beforeIndex = next.findIndex((value) => value.id === item.id);
                            const insertIndex = beforeIndex === -1 ? next.length : beforeIndex;
                            next.splice(insertIndex, 0, moved);
                            return next;
                          });
                            markTeacherEdited("blueprint");
                            setLessonBlueprintArcDragOverId(null);
                            setLessonBlueprintArcDraggingId(null);
                          }}
                        />
                        <div
                          className={`lesson-blueprint-arc-item planner-eqeu-bubble${lessonBlueprintArcDraggingId === item.id ? " is-dragging" : ""}`}
                          data-bubble-id={item.id}
                          style={{
                            "--planner-eqeu-fallback-bg": "var(--color-background-alt)",
                            display: "inline-flex",
                            alignItems: "flex-start",
                            gap: "0.5rem",
                            minHeight: "36px",
                            maxWidth: "100%",
                            flex: "1 1 100%",
                          }}
                        >
                          <button
                            type="button"
                          className="lesson-blueprint-arc-handle"
                          draggable
                          onDragStart={(event) => {
                            event.dataTransfer.setData(
                              "text/plain",
                              JSON.stringify({ id: item.id, source: "arc" })
                            );
                            event.dataTransfer.effectAllowed = "move";
                            setLessonBlueprintArcDraggingId(item.id);
                          }}
                            onDragEnd={() => {
                              setLessonBlueprintArcDraggingId(null);
                              setLessonBlueprintArcDragOverId(null);
                            }}
                            aria-label="Drag lesson arc step"
                          >
                            ⋮⋮
                          </button>
                          <div
                            className="planner-eqeu-bubble-text"
                            contentEditable
                            suppressContentEditableWarning
                            data-placeholder={!item.text ? "Enter lesson arc step..." : ""}
                            onClick={(event) => {
                              const target = event.target;
                              if (!(target instanceof HTMLElement)) return;
                              const link = target.closest("a");
                              if (link?.href) {
                                event.preventDefault();
                                event.stopPropagation();
                                window.open(link.href, "_blank", "noopener,noreferrer");
                              }
                            }}
                            onBlur={(event) => {
                              const text = event.currentTarget.textContent?.trim() || "";
                              if (!text) {
                                setLessonBlueprintArc((prev) =>
                                  prev.filter((value) => value.id !== item.id)
                                );
                                markTeacherEdited("blueprint");
                              } else if (text !== item.text) {
                                setLessonBlueprintArc((prev) =>
                                  prev.map((value) =>
                                    value.id === item.id ? { ...value, text } : value
                                  )
                                );
                                markTeacherEdited("blueprint");
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                event.currentTarget.blur();
                              }
                            }}
                            style={{
                              outline: "none",
                              flex: "1 1 auto",
                              minWidth: "120px",
                              whiteSpace: item.expanded ? "pre-wrap" : "nowrap",
                              wordBreak: "break-word",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            dangerouslySetInnerHTML={{ __html: linkifyText(item.text || "") }}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setLessonBlueprintArc((prev) =>
                                prev.map((value) =>
                                  value.id === item.id
                                    ? { ...value, expanded: !value.expanded }
                                    : value
                                )
                              )
                            }
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                            }}
                            aria-label={item.expanded ? "Show less" : "Show more"}
                            className="planner-eqeu-bubble-action"
                          >
                            {item.expanded ? "▾" : "▸"}
                          </button>
                        </div>
                        </React.Fragment>
                      );
                      })}
                    </div>
                    <div
                      className={`lesson-blueprint-arc-workspace${lessonBlueprintArcDragOverId === "workspace" ? " is-drag-over" : ""}`}
                      onDragEnter={() => setLessonBlueprintArcDragOverId("workspace")}
                      onDragLeave={() => setLessonBlueprintArcDragOverId(null)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        const payload = parseDragPayload(
                          event.dataTransfer.getData("text/plain")
                        );
                        if (!payload?.id) return;
                        if (payload.source === "workspace") {
                          setLessonBlueprintWorkspaceItems((prev) => {
                            const fromIndex = prev.findIndex(
                              (value) => value.id === payload.id
                            );
                            if (fromIndex === -1) return prev;
                            const next = [...prev];
                            const [moved] = next.splice(fromIndex, 1);
                            next.push(moved);
                            return next;
                          });
                        } else {
                          setLessonBlueprintArc((prev) => {
                            const fromIndex = prev.findIndex(
                              (value) => value.id === payload.id
                            );
                            if (fromIndex === -1) return prev;
                            const next = [...prev];
                            const [moved] = next.splice(fromIndex, 1);
                            setLessonBlueprintWorkspaceItems((workspacePrev) =>
                              workspacePrev.concat(moved)
                            );
                            return next;
                          });
                        }
                        markTeacherEdited("blueprint");
                        setLessonBlueprintArcDragOverId(null);
                        setLessonBlueprintArcDraggingId(null);
                      }}
                    >
                      <div className="lesson-blueprint-arc-workspace__title">
                        Learning activity work area
                      </div>
                      <div className="lesson-blueprint-arc-workspace__body">
                        Place unused learning activities here to help organize your planning.
                      </div>
                      {lessonBlueprintWorkspaceItems.length > 0 && (
                        <div className="lesson-blueprint-arc-workspace__items">
                          {lessonBlueprintWorkspaceItems.map((item) => (
                            <div
                              key={item.id}
                              className={`lesson-blueprint-workspace-item planner-eqeu-bubble${lessonBlueprintArcDraggingId === item.id ? " is-dragging" : ""}`}
                              data-bubble-id={item.id}
                              style={{
                                "--planner-eqeu-fallback-bg": "var(--color-background)",
                                display: "inline-flex",
                                alignItems: "flex-start",
                                gap: "0.5rem",
                                minHeight: "36px",
                                maxWidth: "100%",
                                flex: "1 1 100%",
                              }}
                            >
                              <button
                                type="button"
                                className="lesson-blueprint-arc-handle"
                                draggable
                                onDragStart={(event) => {
                                  event.dataTransfer.setData(
                                    "text/plain",
                                    JSON.stringify({ id: item.id, source: "workspace" })
                                  );
                                  event.dataTransfer.effectAllowed = "move";
                                  setLessonBlueprintArcDraggingId(item.id);
                                }}
                                onDragEnd={() => {
                                  setLessonBlueprintArcDraggingId(null);
                                  setLessonBlueprintArcDragOverId(null);
                                }}
                                aria-label="Drag workspace lesson arc step"
                              >
                                ⋮⋮
                              </button>
                              <div
                                className="planner-eqeu-bubble-text"
                                contentEditable
                                suppressContentEditableWarning
                                data-placeholder={!item.text ? "Enter learning activity..." : ""}
                                onClick={(event) => {
                                  const target = event.target;
                                  if (!(target instanceof HTMLElement)) return;
                                  const link = target.closest("a");
                                  if (link?.href) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    window.open(link.href, "_blank", "noopener,noreferrer");
                                  }
                                }}
                                onBlur={(event) => {
                                  const text = event.currentTarget.textContent?.trim() || "";
                                  if (!text) {
                                    setLessonBlueprintWorkspaceItems((prev) =>
                                      prev.filter((value) => value.id !== item.id)
                                    );
                                    markTeacherEdited("blueprint");
                                  } else if (text !== item.text) {
                                    setLessonBlueprintWorkspaceItems((prev) =>
                                      prev.map((value) =>
                                        value.id === item.id ? { ...value, text } : value
                                      )
                                    );
                                    markTeacherEdited("blueprint");
                                  }
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                  }
                                }}
                                style={{
                                  outline: "none",
                                  flex: "1 1 auto",
                                  minWidth: "120px",
                                  whiteSpace: item.expanded ? "pre-wrap" : "nowrap",
                                  wordBreak: "break-word",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                dangerouslySetInnerHTML={{ __html: linkifyText(item.text || "") }}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setLessonBlueprintWorkspaceItems((prev) =>
                                    prev.map((value) =>
                                      value.id === item.id
                                        ? { ...value, expanded: !value.expanded }
                                        : value
                                    )
                                  )
                                }
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                }}
                                aria-label={item.expanded ? "Show less" : "Show more"}
                                className="planner-eqeu-bubble-action"
                              >
                                {item.expanded ? "▾" : "▸"}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="lesson-blueprint-arc-workspace__dropfill" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      onClick={() =>
                        {
                          setLessonBlueprintArc((prev) => [...prev, createEmptyBubbleItem()]);
                          markTeacherEdited("blueprint");
                        }
                      }
                    >
                      + Create a New Learning Activity
                    </Button>
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                {renderGenerationActionPanel({
                  maxWidth: "960px",
                  secondaryAction: {
                    label: "Regenerate Blueprint",
                    loadingLabel: "Generating...",
                    color: "success",
                    outline: true,
                    onClick: handleGenerateBlueprint,
                    disabled:
                      blueprintLoading ||
                      sensitivePlanningBlocked ||
                      isVaporwaveExitActive,
                    title: sensitivePlanningBlocked
                      ? sensitivePlanningBlockedMessage
                      : undefined,
                    loading: blueprintLoading,
                  },
                  primaryAction: {
                    label: "Generate Lesson Plan",
                    onClick: handleGenerate,
                    disabled:
                      !canGenerate ||
                      !hasLessonBlueprint ||
                      sensitivePlanningBlocked ||
                      isVaporwaveExitActive,
                    title: !hasLessonBlueprint
                      ? "Generate a blueprint to continue."
                      : undefined,
                    loading,
                    loadingLabel: "Generating Lesson Plan...",
                  },
                })}
              </div>
            </div>
          )}
        </>
      )}
    </>,
    plannerPromptState,
    plannerShellRef,
    hideShellScene
  );
};

export default SimpleLessonPlanner;
